/**
 * Vaperite Staging Dashboard  (formerly SKC)
 *
 * TODO(vaperite-migration): SKC is being replaced by Vaperite — same staging/auto-update flow, but
 * the menu will be FETCHED from Vaperite rather than received via a menu_sync webhook (stock still
 * arrives via item webhooks). For now this is a display relabel only; the component name
 * (VaperiteStagingDashboard), file, route (/skc/staging), the VaperiteCategoryMappings child, and the backend
 * happen during the Vaperite cutover. See the Vaperite migration spec.
 *
 * Magazine-quality admin dashboard for managing staged partner menu items.
 * Dark fintech aesthetic with glassmorphism, glow effects, and animations.
 *
 * Access: Owner and Admin roles only
 *
 * Tabs:
 * - Staged Items: New items awaiting approval/rejection
 * - Field Changes: Pending field updates for existing products
 * - Processing Rules: Auto-approve vs staged classification rules
 * (Webhook payloads moved to Admin Tools → "Vaperite webhook payloads".)
 */

import { VAPERITE_SELL_IN_APP_TAG } from './vaperite/sellInAppTag';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Paper,
  Skeleton,
  Fade,
  Collapse,
  Switch,
  FormControlLabel,
  alpha,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandIcon,
  Image as ImageIcon,
  Store as StoreIcon,
  Category as CategoryIcon,
  BrandingWatermark as BrandIcon,
  Tune as TuneIcon,
  NewReleases as NewItemIcon,
  Description as DescriptionIcon,
  AttachMoney as PriceIcon,
  Crop as CropIcon,
  LocalOffer as LocalOfferIcon,
  Flag as FlagIcon,
  Block as BlockIcon,
  Rule as RuleIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { Title, useNotify } from 'react-admin';
import authInstance from '../services/httpClient';
import {
  useVaperiteProcessingProgress,
  VaperiteProcessingProgressPanel,
} from '../components/VaperiteProcessingProgress';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { usePermissions } from '../context/PermissionContext';
import { useAdminTheme, AdminColors, StatusDot } from '../admin-theme';
import { scrollToListTop } from '../hooks/useScrollToListTop';
import Logger from '../utils/Logger';
import VaperiteCategoryMappings from './VaperiteCategoryMappings';
import BrandMappingsTab from './vaperite/BrandMappingsTab';
import ResolveUnmappedCategoryDialog from '../components/ResolveUnmappedCategoryDialog';
import RuleOptionRows from './vaperite/RuleOptionRows';
import TagsTab from './vaperite/TagsTab';
import MissingValueRulesTab from './vaperite/MissingValueRulesTab';
import FlaggedItemsTab from './vaperite/FlaggedItemsTab';
import ExcludedItemsTab from './vaperite/ExcludedItemsTab';
import LiveMissingTab from './vaperite/LiveMissingTab';
import NoWoocommerceDialog, { NoWooItem, NO_WOOCOMMERCE_REASON } from './vaperite/NoWoocommerceDialog';
import StagedItemEditDialog, { StagedEditable } from './vaperite/StagedItemEditDialog';
import PendingReviewColumnsButton, { usePendingReviewColumns } from './vaperite/PendingReviewColumns';
import StagedItemRuleCells, { STAGED_ITEM_RULE_HEADERS, StagedItemRuleFields } from './vaperite/StagedItemRuleCells';
import PendingReviewFilters, {
  PendingReviewFilterValue,
  pendingReviewFilterParams,
} from './vaperite/PendingReviewFilters';
import {
  DashboardMetrics,
  GlowCard,
  SystemHealthPanel,
  darkTableSx,
  formatNumber,
  formatTimeAgo,
} from './vaperite/ui';
import OverrideConflict from './vaperite/OverrideConflict';
import { stagingApiBase, stagingOrgLabel, stagingOrg } from './vaperite/stagingOrg';
import { NEW_ITEM, REMOVAL, planApproval, type ApprovalPlan } from './vaperite/approvalChoice';

// ============================================================================
// Types — camelCase to match backend staging-repository.ts
// ============================================================================

// Pending Review has 15 columns and scrolls sideways: the Actions column stays pinned
// to the right edge so Approve / Edit / Reject are always in reach. The body cell
// inherits its row's background (incl. the hover colour) so scrolled cells do not
// show through; the header cell sits above both the sticky header row and the body.
const STICKY_ACTIONS_CELL_SX = {
  position: 'sticky',
  right: 0,
  zIndex: 1,
  bgcolor: 'inherit',
  boxShadow: '-6px 0 6px -6px rgba(0, 0, 0, 0.45)',
} as const;
const STICKY_ACTIONS_HEAD_SX = { ...STICKY_ACTIONS_CELL_SX, zIndex: 3 } as const;

interface StagingSummary {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  withDiscrepancies: number;
  pendingNewItems: number;
  flagged?: number;
  excluded?: number;
  uniqueStores: string[];
  fieldChangeCounts?: Record<string, number>;
}

interface StagedItem extends StagedItemRuleFields {
  id: number;
  sku: string;
  suggestedName: string;
  suggestedDescription: string | null;
  suggestedImageUrl: string | null;
  basePrice: number | null;
  status: 'pending' | 'approved' | 'rejected';
  hasPriceDiscrepancies: boolean;
  hasDescriptionDiscrepancies: boolean;
  hasImageDiscrepancies: boolean;
  hasCriticalErrors: boolean;
  stagedAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  storeCount?: number;
  type: 'new_item' | 'field_update' | 'removal';
  changeCount: number;
  changedFields: string[];
  stores?: Array<{
    externalStoreId: string;
    storePrice: number | null;
    suggestedStockLevel: number;
  }>;
  categories?: Array<{
    payloadCategoryId: string | null;
    payloadCategoryName: string | null;
    contributingStores: string[] | null;
  }>;
  existingProductId?: string | null;
  /** Barcode (EAN). */
  gtin?: string | null;
  /** Comma-separated tag names, as stored by the sync. */
  webhookTags?: string | null;
  /** Present on list rows; on detail it lives in canonicalItemData.brand. */
  brand?: string | null;
  categoryNames?: string[];
  /** The Vend category when none of the item's categories is mapped (list rows). */
  unmappedCategory?: string | null;
  /** Detail only: a reviewer set sell-in-app, so the feed no longer refreshes it. */
  sellInAppOverridden?: boolean;
  canonicalItemData?: Record<string, unknown>;
  discrepancyDetails?: Array<{
    field: string;
    stores: Array<{ externalStoreId: string; value: string | null }>;
  }>;
  fieldChanges?: FieldChangeDetail[];
}

interface FieldChangeDetail {
  id: number | null;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  externalStoreId: string | null;
  createdAt: string | null;
  /** All DB row IDs covered by this deduplicated entry. */
  allIds?: number[];
  /** Perceptual hash comparison debug info (image_url fields only). */
  imageCompareResult?: { result?: string; distance?: number; reason?: string; oldHash?: string; newHash?: string; ts?: string } | null;
  /** Lightspeed changed a field the admin overrode: resolved one by one (Take / Keep), never in bulk. */
  overrideConflict?: boolean;
  overrideValue?: string | null;
  /** Category conflicts: names for the stored category ids. */
  displayValues?: { old: string | null; new: string | null; override: string | null };
}

/** Bulk approve skips override conflicts server-side, so they are never selectable. */
const isBulkApprovable = (fc: FieldChangeDetail) => fc.id != null && !fc.overrideConflict;

const EVENT_TYPE_LABELS: Record<string, string> = {
  menu_sync: 'Menu Sync',
  menu_update: 'Menu Update',
  item_update: 'Item Update',
  'orders.notification': 'Order Notification',
  'delivery.state_changed': 'Delivery State Change',
};

function formatEventType(eventType: string | null | undefined): string {
  if (!eventType) return 'Menu Sync';
  return EVENT_TYPE_LABELS[eventType] ?? eventType.replace(/[_.]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ============================================================================
// Constants & Chart Colors
// ============================================================================

const PIE_COLORS = [AdminColors.warning, AdminColors.error, AdminColors.info, AdminColors.purple];

const ITEMS_PER_PAGE = 50;

// ============================================================================
// Helpers
// ============================================================================

/** Backend stores DECIMAL(10,2) — actual currency, NOT cents */
function formatPrice(value: number | null): string {
  if (value === null || value === undefined) return '-';
  return `R${Number(value).toFixed(2)}`;
}

// ============================================================================
// Tab Panel
// ============================================================================

function TabPanel({ children, value, index }: { children?: React.ReactNode; index: number; value: number }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Fade in timeout={300}><Box sx={{ py: 3 }}>{children}</Box></Fade>}
    </div>
  );
}

// ============================================================================
// Loading Skeleton
// ============================================================================

function DashboardSkeleton() {
  const { colors } = useAdminTheme();
  return (
    <Box sx={{ p: 3, bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
      <Skeleton variant="text" width={320} height={44} sx={{ bgcolor: colors.bgCard }} />
      <Skeleton variant="text" width={200} height={20} sx={{ bgcolor: colors.bgCard, mb: 3 }} />
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {[1, 2, 3, 4].map((i) => (
          <Grid size={{ xs: 6, md: 3 }} key={i}>
            <Skeleton variant="rounded" height={100} sx={{ bgcolor: colors.bgCard, borderRadius: 3 }} />
          </Grid>
        ))}
      </Grid>
      <Skeleton variant="rounded" height={48} sx={{ bgcolor: colors.bgCard, mb: 3 }} />
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} variant="rounded" height={52} sx={{ bgcolor: colors.bgCard, mb: 0.5, borderRadius: 1 }} />
      ))}
    </Box>
  );
}

// ============================================================================
// Status Chip (with glow)
// ============================================================================

function StatusChip({ status }: { status: string }) {
  const { colors } = useAdminTheme();
  const map: Record<string, { color: string; bg: string }> = {
    pending: { color: colors.warning, bg: alpha(colors.warning, 0.12) },
    approved: { color: colors.success, bg: alpha(colors.success, 0.12) },
    rejected: { color: colors.error, bg: alpha(colors.error, 0.12) },
  };
  const s = map[status] || { color: colors.textMuted, bg: colors.bgElevated };
  return (
    <Chip
      label={status.toUpperCase()}
      size="small"
      sx={{
        bgcolor: s.bg,
        color: s.color,
        fontWeight: 700,
        fontSize: '0.65rem',
        letterSpacing: '0.05em',
        border: `1px solid ${alpha(s.color, 0.3)}`,
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: `0 0 12px ${alpha(s.color, 0.3)}`,
        },
      }}
    />
  );
}

// ============================================================================
// Sortable Header
// ============================================================================

/** Pending Review rule columns that sort on a header click, like SKU / Name / Price. */
const RULE_SORT_FIELDS: Partial<Record<string, string>> = {
  'Vend type': 'vendType',
  Category: 'category',
  Tags: 'tags',
  Brand: 'brand',
};

/** The text a rule column sorts on (what the cell shows), lower-cased; '' when blank. */
function ruleSortValue(item: StagedItemRuleFields, field: string): string {
  switch (field) {
    case 'vendType': return (item.categoryPath ?? []).join(' > ').toLowerCase();
    case 'category': return item.resolvedCategory?.held ? '' : (item.resolvedCategory?.name ?? '').toLowerCase();
    case 'tags': return (item.tags ?? []).join(', ').toLowerCase();
    case 'brand': return (item.brand ?? '').toLowerCase();
    default: return '';
  }
}

function SortableHeader({ field, label, currentSort, currentDir, onSort }: {
  field: string; label: string; currentSort: string; currentDir: 'asc' | 'desc'; onSort: (f: string) => void;
}) {
  const { colors } = useAdminTheme();
  const active = currentSort === field;
  return (
    <TableCell
      onClick={() => onSort(field)}
      sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { color: colors.info } }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <span>{label}</span>
        {active && (
          <Typography variant="caption" sx={{ color: colors.info, fontSize: '0.6rem' }}>
            {currentDir === 'asc' ? '\u25B2' : '\u25BC'}
          </Typography>
        )}
      </Stack>
    </TableCell>
  );
}

// ============================================================================
// Staged Field Block
// ============================================================================

/**
 * One labelled field in the staged-item detail panel.
 *
 * Renders even when empty — a reviewer has to be able to tell "this product has no
 * brand" apart from "the brand field isn't shown here", and a conditionally hidden
 * field cannot express the difference. Empty shows an em dash, and where we have a
 * suggestion from similar existing products it is offered underneath, clearly marked
 * as a suggestion. Nothing here applies a suggestion; that is Edit's job.
 */
function StagedFieldBlock({
  label,
  value,
  suggestion = null,
  mono = false,
}: {
  label: string;
  value?: string | null;
  suggestion?: { value: string; reason: string } | null;
  mono?: boolean;
}) {
  const { colors } = useAdminTheme();
  const hasValue = value != null && String(value).trim() !== '';

  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: hasValue ? colors.textSecondary : colors.textMuted,
          fontFamily: mono && hasValue ? 'monospace' : undefined,
          fontStyle: hasValue ? undefined : 'italic',
        }}
      >
        {hasValue ? value : '—'}
      </Typography>
      {!hasValue && suggestion && (
        <Tooltip title={suggestion.reason}>
          <Chip
            size="small"
            label={`Suggested: ${suggestion.value}`}
            sx={{
              mt: 0.5,
              height: 20,
              fontSize: '0.6rem',
              color: colors.info,
              bgcolor: alpha(colors.info, 0.1),
              border: `1px dashed ${alpha(colors.info, 0.4)}`,
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

// ============================================================================
// Discrepancy Chips
// ============================================================================

function DiscrepancyChips({ item }: { item: StagedItem }) {
  const { colors } = useAdminTheme();
  const chips: Array<{ label: string; color: string }> = [];
  if (item.hasCriticalErrors) chips.push({ label: 'ERROR', color: colors.error });
  if (item.hasPriceDiscrepancies) chips.push({ label: 'Price', color: colors.warning });
  if (item.hasDescriptionDiscrepancies) chips.push({ label: 'Desc', color: colors.info });
  if (item.hasImageDiscrepancies) chips.push({ label: 'Image', color: colors.purple });

  if (chips.length === 0) {
    return <Typography variant="caption" sx={{ color: colors.textMuted }}>None</Typography>;
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {chips.map((c) => (
        <Chip
          key={c.label}
          label={c.label}
          size="small"
          sx={{
            bgcolor: alpha(c.color, 0.12),
            color: c.color,
            fontWeight: 600,
            fontSize: '0.6rem',
            height: 22,
            border: `1px solid ${alpha(c.color, 0.25)}`,
            ...(c.label === 'ERROR' ? { animation: 'skc-pulse 2s ease-in-out infinite' } : {}),
          }}
        />
      ))}
    </Stack>
  );
}

// ============================================================================
// Main Component
// ============================================================================

const VaperiteStagingDashboard: React.FC = () => {
  const { colors, glassHeaderStyle, elevatedInputStyle } = useAdminTheme();
  const { role, loading: permissionsLoading } = usePermissions();
  const notify = useNotify();

  const [activeTab, setActiveTab] = useState(0);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // Summary
  const [summary, setSummary] = useState<StagingSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  // Live: still missing tab count (its own endpoint; null until it loads or if it fails)
  const [liveMissingTotal, setLiveMissingTotal] = useState<number | null>(null);

  // Staged items
  const [items, setItems] = useState<StagedItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsPage, setItemsPage] = useState(1);
  /** Scroll container for the staged-items table — reset to the top on page change. */
  const itemsTableRef = useRef<HTMLDivElement>(null);
  const [itemsStatus, setItemsStatus] = useState<string>('pending');
  const [itemsSearch, setItemsSearch] = useState('');
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set());

  // Change type filter
  const [changeTypeFilter, setChangeTypeFilter] = useState<string>('');
  // Rule filters: unmapped Vend type / no woocommerce tag (sell_in_app=false).
  const [ruleFilters, setRuleFilters] = useState<PendingReviewFilterValue>({});

  // Detail dialog — field change selection
  const [selectedFieldChangeIds, setSelectedFieldChangeIds] = useState<Set<number>>(new Set());

  // Detail dialog
  const [detailItem, setDetailItem] = useState<StagedItem | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [skcTrimPreview, setSkcTrimPreview] = useState<{
    original: string;
    trimmed: string;
    trimInfo: Record<string, any>;
  } | null>(null);
  const [skcTrimLoading, setSkcTrimLoading] = useState(false);

  // Reject dialog
  const [rejectSku, setRejectSku] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Edit dialog (shared with Flagged Items) — correct a pending item's fields before
  // approving it. Tracked separately from detailItem so Edit works straight from a
  // table row, without the detail dialog having been opened first.
  const [editItem, setEditItem] = useState<StagedEditable | null>(null);

  // Suggested brand/category/tags derived from similar existing products.
  const [suggestions, setSuggestions] = useState<{
    brand: { value: string; reason: string } | null;
    category: { value: string; reason: string } | null;
    tags: { value: string[]; reason: string } | null;
    description: { value: string; reason: string } | null;
    basePrice: { value: number; reason: string } | null;
    gtin: { value: string; reason: string } | null;
  } | null>(null);

  // Approve dialog
  // No approve dialog: approving is a single click. (Cin7/DEAR is decommissioned —
  // the old dialog's "queue a Cin7 job" copy was stale, and its Cin7-ID field made
  // the backend mark the item approved WITHOUT writing the product row.)

  // Store filter
  const [storeFilter, setStoreFilter] = useState<string>('');

  // Bulk approve dialog
  // Approve picker: open when an approval covers more than one change or a missing value
  // (vaperite/approvalChoice.ts). Every option starts unticked.
  const [approvePlan, setApprovePlan] = useState<{ skus: string[]; plan: ApprovalPlan; bulk: boolean } | null>(null);
  const [approveChoice, setApproveChoice] = useState<Set<string>>(new Set());
  const [bulkApproveCategory, setBulkApproveCategory] = useState('');

  // Bulk reject dialog
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');

  // Processing rules
  // Processing Rules → category → "Stage" unmapped categories: the Vend category being resolved.
  const [resolveVendCategory, setResolveVendCategory] = useState<string | null>(null);
  const [rules, setRules] = useState<Array<{ id: number; fieldName: string; requiresApproval: boolean; mode: string; options: Record<string, boolean>; updatedAt: string }>>([]);
  const holdsUnmapped = rules.find((r) => r.fieldName === 'category')?.options?.holdUnmapped === true;
  const [rulesLoading, setRulesLoading] = useState(false);

  // Sort
  const [sortField, setSortField] = useState<string>('stagedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Payload viewer (detail dialog raw JSON)
  const [payloadOpen, setPayloadOpen] = useState(false);

  // Rich metrics
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);

  // Processing progress (shared Socket.IO live panel). Display only: data
  // refetches happen on page load and "Refresh all data", never on a socket event.
  const processingProgress = useVaperiteProcessingProgress();

  // Pending Review column chooser (persisted in localStorage)
  const pendingColumns = usePendingReviewColumns();

  // ------ Data Fetching ------

  const fetchSummary = useCallback(async () => {
    authInstance.get(`${stagingApiBase()}/staging/live-missing`, { params: { limit: 1 } })
      .then(({ data }) => setLiveMissingTotal(data.total ?? 0))
      .catch((err) => Logger.error('Failed to fetch the live-missing count:', err));
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/summary`, {

      });
      setSummary(data);
    } catch (err: any) {
      Logger.error('Failed to fetch staging summary:', err);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    setItemsLoading(true);
    try {
      const params: Record<string, any> = {
        page: itemsPage,
        limit: ITEMS_PER_PAGE,
      };
      if (itemsStatus) params.status = itemsStatus;
      if (itemsSearch.trim()) params.search = itemsSearch.trim();
      if (storeFilter) params.store_id = storeFilter;
      if (changeTypeFilter) params.change_type = changeTypeFilter;
      Object.assign(params, pendingReviewFilterParams(ruleFilters));

      const { data } = await authInstance.get(`${stagingApiBase()}/staging/items`, {
        params,
      });
      setItems(data.items || []);
      setItemsTotal(data.total || 0);
      setSelectedSkus(new Set());
    } catch (err: any) {
      Logger.error('Failed to fetch staged items:', err);
      notify('Failed to load staged items', { type: 'error' });
    } finally {
      setItemsLoading(false);
    }
  }, [itemsPage, itemsStatus, itemsSearch, storeFilter, changeTypeFilter, ruleFilters, notify]);

  const fetchRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/rules`, {

      });
      setRules(data.rules || []);
    } catch (err: any) {
      Logger.error('Failed to fetch rules:', err);
    } finally {
      setRulesLoading(false);
    }
  }, []);

  const fetchMetrics = useCallback(async () => {
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/metrics`);
      setMetrics(data);
    } catch (err: any) {
      Logger.error('Failed to fetch metrics:', err);
    }
  }, []);

  const refreshAll = useCallback(() => {
    fetchSummary();
    fetchItems();
    fetchMetrics();
    if (activeTab === 1) fetchRules();
    setLastUpdate(new Date());
  }, [fetchSummary, fetchItems, fetchMetrics, fetchRules, activeTab]);

  useEffect(() => { fetchSummary(); fetchItems(); fetchRules(); fetchMetrics(); }, [fetchSummary, fetchItems, fetchRules, fetchMetrics]);
  // Also reload rules when entering the rules tab
  useEffect(() => {
    if (activeTab === 1) fetchRules();
  }, [activeTab, fetchRules]);

  // ------ Actions ------

  /** Open the shared edit dialog on the item currently on screen. */
  const handleOpenEdit = (item: StagedItem) => {
    const canonicalBrand = (item.canonicalItemData as { brand?: string } | undefined)?.brand;
    setEditItem({
      sku: item.sku,
      type: item.type,
      name: item.suggestedName ?? '',
      description: item.suggestedDescription ?? null,
      basePrice: item.basePrice ?? null,
      category: item.categories?.[0]?.payloadCategoryName ?? item.categoryNames?.[0] ?? null,
      tags: item.webhookTags ?? null,
      brand: item.brand ?? canonicalBrand ?? null,
      gtin: item.gtin ?? null,
      imageUrl: item.suggestedImageUrl ?? null,
      sellInApp: item.sellInApp !== false,
      isRestricted: item.isRestricted === true,
      // Only the detail response carries this; list rows leave it undefined.
      sellInAppOverridden: item.sellInAppOverridden === true,
    });
  };

  const handleEditSaved = (saved?: StagedItem) => {
    // Render what was actually stored rather than the optimistic local edit —
    // but only if the detail dialog is showing this same SKU.
    if (saved && detailItem?.sku === saved.sku) setDetailItem(saved);
    refreshAll();
  };

  // Approvals the server held because the item has no woocommerce tag in Vend: the
  // person approves anyway, excludes, or skips. 'all' is a count from Approve All.
  const [noWoo, setNoWoo] = useState<{
    kind: 'single' | 'bulk' | 'all'; items: NoWooItem[]; total: number;
    /** Bulk only: the category override chosen for the batch, kept for "approve anyway". */
    category?: string;
  } | null>(null);
  const firstError = (data: any) => data?.error || data?.results?.find((r: any) => !r.success && !r.noWoocommerce)?.error;
  const [noWooBusy, setNoWooBusy] = useState(false);

  const handleApprove = async (sku: string, allowNoWoocommerce = false) => {
    try {
      const item = items.find(i => i.sku === sku);

      if (item?.type === 'field_update') {
        // Existing product — approve field changes, don't re-create
        await authInstance.post(
          `${stagingApiBase()}/staging/field-changes/bulk-approve`,
          { skus: [sku] },
        );
      } else {
        // New product — queue the write into the Deeliver product table.
        // No confirmation step: Cin7/DEAR is decommissioned, so there is nothing
        // to ask about. The category comes from skc_category_mappings; if none
        // resolves the API answers 422 and the message below shows why.
        await authInstance.post(
          `${stagingApiBase()}/staging/items/${sku}/approve`,
          allowNoWoocommerce ? { allowNoWoocommerce } : {},
        );
      }
      notify(`${sku} approved`, { type: 'success' });
      refreshAll();
    } catch (err: any) {
      if (err.response?.data?.noWoocommerce) {
        setNoWoo({ kind: 'single', items: [{ sku, name: err.response.data.name ?? sku }], total: 1 });
        return;
      }
      // Held by Processing Rules (category → Stage): go straight to the map-or-create decision.
      const unmapped = err.response?.data?.unmappedCategory;
      if (unmapped) {
        setResolveVendCategory(unmapped);
        return;
      }
      notify(`Failed to approve: ${err.response?.data?.error || err.message}`, { type: 'error' });
    }
  };

  const handleReject = async () => {
    if (!rejectSku || !rejectReason.trim()) return;
    try {
      await authInstance.post(
        `${stagingApiBase()}/staging/items/${rejectSku}/reject`,
        { reason: rejectReason },
      );
      notify(`${rejectSku} rejected`, { type: 'success' });
      setRejectSku(null);
      setRejectReason('');
      refreshAll();
    } catch (err: any) {
      notify(`Failed to reject: ${err.response?.data?.error || err.message}`, { type: 'error' });
    }
  };

  /** Approve straight through, or open the picker when there is a choice to make. */
  const requestApprove = (skus: string[], bulk: boolean, known?: StagedItem) => {
    const rows = known ? [known] : items.filter((i) => skus.includes(i.sku));
    const plan = planApproval(rows);
    if (!plan.needsChoice) {
      // Send the change types the rows SHOW, so a change that is not on screen (hidden by
      // a change-type filter, or synced in since the page loaded) is never approved with them.
      if (bulk) handleBulkApprove({ skus, choice: new Set(plan.options.map((o) => o.key)) });
      else handleApprove(skus[0]);
      return;
    }
    setApproveChoice(new Set());
    setApprovePlan({ skus, plan, bulk });
  };

  /**
   * Approve rows. `choice` limits it to the picked change types (NEW_ITEM = create the new
   * items); null approves everything on the rows, as the straight-through path does.
   */
  const handleBulkApprove = async (
    { skus = Array.from(selectedSkus), choice = null, bulk = true }: { skus?: string[]; choice?: Set<string> | null; bulk?: boolean } = {},
  ) => {
    // Read the org once: these run several requests, and switching Vaperite <-> Deeliver
    // mid-run must not send the rest of the batch to the other org's queue.
    const apiBase = stagingApiBase();
    if (skus.length === 0) return;
    const skuSet = new Set(skus);
    const fieldNames = choice ? Array.from(choice).filter((c) => c !== NEW_ITEM && c !== REMOVAL) : null;
    try {
      // Split selected items into new items and field-update items, keeping only what was picked
      const newItemSkus = items
        .filter(i => skuSet.has(i.sku) && i.type !== 'field_update')
        .filter(i => !choice || choice.has(i.type === 'removal' ? REMOVAL : NEW_ITEM))
        .map(i => i.sku);
      const fieldUpdateSkus = items
        .filter(i => skuSet.has(i.sku) && i.type === 'field_update')
        .filter(i => !fieldNames || (i.changedFields ?? []).some((f) => fieldNames.includes(f)))
        .map(i => i.sku);

      const promises: Promise<any>[] = [];

      // Held items (no woocommerce tag) come back in noWoocommerce — on a 200, or on the
      // 422 when nothing else was approvable either.
      let held: NoWooItem[] = [];
      let newApproved = 0;
      let newFailed = 0;
      let failReason: string | undefined;
      const category = bulkApproveCategory || undefined;
      if (newItemSkus.length > 0) {
        const take = (data: any) => {
          held = data?.noWoocommerce ?? [];
          newApproved = data?.approved ?? 0;
          // Held items are not failures: they wait for the dialog below.
          newFailed = Math.max(0, (data?.failed ?? 0) - held.length);
          failReason = firstError(data);
        };
        promises.push(authInstance.post(
          `${apiBase}/staging/items/bulk-approve`,
          { skus: newItemSkus, category },
        ).then((res) => take(res.data))
          .catch((err) => {
            // A 422 is "nothing approved": still read it, so held items are asked about and
            // real failures are reported rather than hidden behind them.
            if (err.response?.status !== 422) throw err;
            take(err.response.data);
          }));
      }

      if (fieldUpdateSkus.length > 0) {
        // Send SKUs to bulk-approve — backend resolves field change IDs.
        // (List view doesn't pre-load fieldChanges with IDs, only detail view does.)
        promises.push(authInstance.post(
          `${apiBase}/staging/field-changes/bulk-approve`,
          fieldNames ? { skus: fieldUpdateSkus, fieldNames } : { skus: fieldUpdateSkus },
        ));
      }

      await Promise.all(promises);
      const approvedCount = newApproved + fieldUpdateSkus.length;
      if (newFailed > 0) {
        notify(`${approvedCount} approved, ${newFailed} failed${failReason ? `: ${failReason}` : ''}`, { type: 'warning' });
      } else if (approvedCount > 0) {
        notify(`${approvedCount} items approved`, { type: 'success' });
      }
      if (held.length > 0) setNoWoo({ kind: 'bulk', items: held, total: held.length, category });
      if (bulk) setSelectedSkus(new Set());
      setApprovePlan(null);
      setBulkApproveCategory('');
      refreshAll();
    } catch (err: any) {
      notify(`Bulk approve failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    }
  };

  const [approveAllLoading, setApproveAllLoading] = useState(false);

  // Approve All works on the whole queue, never on the filtered view: it is refused
  // while a rule filter is on, so a reviewer looking at (say) unmapped types cannot
  // mistake it for "approve these".
  const ruleFilterActive = ruleFilters.unmapped === true || ruleFilters.sellInApp === false;

  const handleApproveAll = async () => {
    // Read the org once: these run several requests, and switching Vaperite <-> Deeliver
    // mid-run must not send the rest of the batch to the other org's queue.
    const apiBase = stagingApiBase();
    const pendingCount = summary?.pending ?? itemsTotal;
    if (!window.confirm(
      `This ignores your filters and will approve ALL ${pendingCount} pending items, `
      + 'plus every pending field change (override conflicts excepted). '
      + (stagingOrg() === 'deeliver' ? 'Continue?' : `Items without the ${VAPERITE_SELL_IN_APP_TAG} tag in Vend are held for you to decide. Continue?`),
    )) return;
    setApproveAllLoading(true);
    try {
      // NEW ITEMS FIRST — this is what the button used to miss entirely. It only
      // called field-changes/approve-all, so on a queue of new items it approved
      // nothing and reported "Approved 0 changes".
      //
      // The endpoint is bounded per call (approving writes a product row and
      // enqueues a job, and the ALB drops an idle connection at 120s), so loop
      // until it reports nothing remaining.
      let approvedItems = 0;
      let failedItems = 0;
      let heldNoWoo = 0;
      // Belt and braces: `remaining` is what ends the loop, but a run of batches
      // that approves nothing would otherwise spin forever on a stuck row.
      for (let guard = 0; guard < 200; guard += 1) {
        const res = await authInstance.post(`${apiBase}/staging/items/approve-all`, { limit: 250 });
        const d = res.data ?? {};
        approvedItems += d.approved ?? 0;
        failedItems += d.failed ?? 0;
        heldNoWoo = d.heldNoWoocommerce ?? heldNoWoo;
        // A batch that approved nothing will be re-selected as-is (failed rows stay pending): stop.
        if (!d.remaining || d.processed === 0 || !d.approved) break;
        notify(`Approving… ${approvedItems} done, ${d.remaining} to go`, { type: 'info' });
      }

      // Then any field changes on existing products. No image exclusion: that was
      // a Cin7 artefact — applying an image change is now a local UPDATE, not a
      // second API upload.
      // Time-bounded per call like the items endpoint, so loop on `remaining` too.
      let approvedChanges = 0;
      for (let guard = 0; guard < 200; guard += 1) {
        // eslint-disable-next-line no-await-in-loop
        const fc = await authInstance.post(`${apiBase}/staging/field-changes/approve-all`, { excludeFields: [] });
        const d = fc.data ?? {};
        approvedChanges += d.approved ?? 0;
        // Failed changes stay pending and come back next call: stop on a call that approved nothing.
        if (!d.remaining || !d.approved) break;
        notify(`Approving field changes… ${approvedChanges} done, ${d.remaining} to go`, { type: 'info' });
      }

      const parts = [];
      if (approvedItems) parts.push(`${approvedItems} item${approvedItems === 1 ? '' : 's'}`);
      if (approvedChanges) parts.push(`${approvedChanges} field change${approvedChanges === 1 ? '' : 's'}`);
      if (failedItems) parts.push(`${failedItems} failed`);

      notify(parts.length ? `Approved ${parts.join(', ')}` : 'Nothing pending to approve', {
        type: failedItems ? 'warning' : 'success',
      });
      if (heldNoWoo > 0) setNoWoo({ kind: 'all', items: [], total: heldNoWoo });
      refreshAll();
    } catch (err: any) {
      notify(`Failed: ${err.response?.data?.error || err.response?.data?.message || err.message}`, { type: 'error' });
    } finally {
      setApproveAllLoading(false);
    }
  };

  const noWooConfirm = async () => {
    // Read the org once: these run several requests, and switching Vaperite <-> Deeliver
    // mid-run must not send the rest of the batch to the other org's queue.
    const apiBase = stagingApiBase();
    if (!noWoo) return;
    setNoWooBusy(true);
    try {
      if (noWoo.kind === 'single') {
        await handleApprove(noWoo.items[0].sku, true);
      } else if (noWoo.kind === 'bulk') {
        const { data } = await authInstance.post(`${apiBase}/staging/items/bulk-approve`, {
          skus: noWoo.items.map((i) => i.sku), category: noWoo.category, allowNoWoocommerce: true,
        }).catch((err) => {
          if (err.response?.status !== 422) throw err;
          return { data: err.response.data };
        });
        const why = firstError(data);
        notify(`${data?.approved ?? 0} approved${data?.failed ? `, ${data.failed} failed${why ? `: ${why}` : ''}` : ''}`, {
          type: data?.failed ? 'warning' : 'success',
        });
      } else {
        let approved = 0;
        let failed = 0;
        for (let guard = 0; guard < 200; guard += 1) {
          // eslint-disable-next-line no-await-in-loop
          const { data: d } = await authInstance.post(`${apiBase}/staging/items/approve-all`, { limit: 250, onlyNoWoocommerce: true });
          approved += d?.approved ?? 0;
          failed += d?.failed ?? 0;
          // Failed rows stay pending and untagged, so a batch with no approvals repeats: stop.
          if (!d?.remaining || d?.processed === 0 || !d?.approved) break;
        }
        notify(`Approved ${approved}${failed ? `, ${failed} failed` : ''}`, { type: failed ? 'warning' : 'success' });
      }
    } catch (err: any) {
      notify(`Failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setNoWooBusy(false);
      if (noWoo.kind === 'bulk') setSelectedSkus(new Set());
      setNoWoo(null);
      refreshAll();
    }
  };

  const noWooExclude = async () => {
    if (!noWoo || noWoo.items.length === 0) return;
    setNoWooBusy(true);
    try {
      const { data } = await authInstance.post(`${stagingApiBase()}/staging/items/bulk-reject`, {
        skus: noWoo.items.map((i) => i.sku), reason: NO_WOOCOMMERCE_REASON,
      });
      notify(`${data?.rejected ?? noWoo.items.length} excluded — see Excluded Products`, { type: 'success' });
    } catch (err: any) {
      notify(`Failed to exclude: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setNoWooBusy(false);
      if (noWoo.kind === 'bulk') setSelectedSkus(new Set());
      setNoWoo(null);
      refreshAll();
    }
  };

  // "Keep hidden" on a pending sellable change: our Sell in app stays off, saved as an
  // override against their woocommerce tag so the sync stops asking.
  const handleKeepHidden = async (changeId: number) => {
    try {
      await authInstance.post(`${stagingApiBase()}/staging/field-changes/${changeId}/keep-override`);
      notify('Kept hidden: Sell in app stays off', { type: 'success' });
      // Other changes on the product may still be pending: show it again rather than close.
      if (detailItem?.sku) handleViewDetail(detailItem.sku);
      refreshAll();
    } catch (err: any) {
      notify(`Failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    }
  };

  const handleApproveFieldChanges = async (ids: number[]) => {
    try {
      await authInstance.post(
        `${stagingApiBase()}/staging/field-changes/bulk-approve`,
        { ids },
      );
      notify(`${ids.length} change${ids.length > 1 ? 's' : ''} approved & queued`, { type: 'success' });
      setDetailOpen(false);
      setSelectedFieldChangeIds(new Set());
      refreshAll();
    } catch (err: any) {
      notify(`Failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    }
  };

  const handleBulkReject = async () => {
    if (selectedSkus.size === 0 || !bulkRejectReason.trim()) return;
    try {
      await authInstance.post(
        `${stagingApiBase()}/staging/items/bulk-reject`,
        { skus: Array.from(selectedSkus), reason: bulkRejectReason },
      );
      notify(`${selectedSkus.size} items rejected`, { type: 'success' });
      setSelectedSkus(new Set());
      setBulkRejectOpen(false);
      setBulkRejectReason('');
      refreshAll();
    } catch (err: any) {
      notify(`Bulk reject failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    }
  };

  const handleSkcSmartTrimPreview = async (imageUrl: string) => {
    setSkcTrimLoading(true);
    setSkcTrimPreview(null);
    try {
      const { data } = await authInstance.post('/admin/tools/smart-trim-preview', {
        imageUrl,
      });
      if (data.success) {
        setSkcTrimPreview(data.data);
      }
    } catch (err: any) {
      notify(`Smart trim preview failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setSkcTrimLoading(false);
    }
  };

  const handleSetRuleMode = async (fieldName: string, mode: string) => {
    try {
      await authInstance.put(
        `${stagingApiBase()}/staging/rules/${fieldName}`,
        { mode },
      );
      notify(`Rule updated: ${fieldName} → ${mode}`, { type: 'success' });
      fetchRules();
    } catch (err: any) {
      notify(`Failed to update rule: ${err.response?.data?.error || err.message}`, { type: 'error' });
    }
  };

  const handleViewDetail = async (sku: string) => {
    setDetailLoading(true);
    setDetailOpen(true);
    setSelectedFieldChangeIds(new Set());
    setSkcTrimPreview(null);
    setSuggestions(null);
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/items/${sku}`);
      setDetailItem(data.item);

      // Suggestions are an assist, not a gate: fetched alongside (not awaited with)
      // the item so a slow or failed lookup never delays or blocks the panel.
      if (data.item?.status === 'pending') {
        authInstance
          .get(`${stagingApiBase()}/staging/items/${sku}/suggestions`)
          .then(({ data: s }) => setSuggestions(s))
          .catch(() => setSuggestions(null));
      }
      // Pre-select all field changes for field_update items
      if (data.item?.fieldChanges) {
        const ids = data.item.fieldChanges
          .filter(isBulkApprovable)
          .map((fc: FieldChangeDetail) => fc.id as number);
        setSelectedFieldChangeIds(new Set(ids));
      }
    } catch (err: any) {
      notify('Failed to load item details', { type: 'error' });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  /**
   * Re-read the open item after an override conflict was resolved. When nothing is
   * left pending the item is gone (404): close the dialog instead of erroring.
   */
  const reloadDetail = async (sku: string) => {
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/items/${sku}`);
      setDetailItem(data.item);
      setSelectedFieldChangeIds((prev) => {
        const still = new Set<number>(
          (data.item?.fieldChanges ?? []).filter(isBulkApprovable).map((fc: FieldChangeDetail) => fc.id as number),
        );
        return new Set(Array.from(prev).filter((id) => still.has(id)));
      });
    } catch (err: any) {
      if (err?.response?.status !== 404) notify('Failed to reload item details', { type: 'error' });
      setDetailOpen(false);
    }
  };

  // ------ Selection ------

  const toggleSelect = (sku: string) => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pendingItems = items.filter((i) => i.status === 'pending');
    if (selectedSkus.size === pendingItems.length) {
      setSelectedSkus(new Set());
    } else {
      setSelectedSkus(new Set(pendingItems.map((i) => i.sku)));
    }
  };

  // ------ Derived data ------

  const pendingItems = items.filter((i) => i.status === 'pending');
  const totalPages = Math.ceil(itemsTotal / ITEMS_PER_PAGE);

  const sortedItems = useMemo(() => {
    const sorted = [...items];
    sorted.sort((a, b) => {
      let aVal: any, bVal: any;
      switch (sortField) {
        case 'sku': aVal = a.sku; bVal = b.sku; break;
        case 'suggestedName': aVal = a.suggestedName; bVal = b.suggestedName; break;
        case 'basePrice': aVal = a.basePrice ?? 0; bVal = b.basePrice ?? 0; break;
        case 'storeCount': aVal = a.storeCount ?? 0; bVal = b.storeCount ?? 0; break;
        case 'stagedAt': aVal = a.stagedAt; bVal = b.stagedAt; break;
        // Text columns sort case-insensitively; blanks go last either way round.
        case 'vendType': case 'category': case 'tags': case 'brand': {
          const av = ruleSortValue(a, sortField);
          const bv = ruleSortValue(b, sortField);
          if (!av || !bv) return av === bv ? 0 : av ? -1 : 1;
          return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        }
        default: aVal = a.stagedAt; bVal = b.stagedAt;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [items, sortField, sortDir]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const discrepancyPieData = useMemo(() => {
    if (!summary || summary.withDiscrepancies === 0) return [];
    // Approximate breakdown from items on screen
    const price = items.filter(i => i.hasPriceDiscrepancies).length;
    const desc = items.filter(i => i.hasDescriptionDiscrepancies).length;
    const img = items.filter(i => i.hasImageDiscrepancies).length;
    const err = items.filter(i => i.hasCriticalErrors).length;
    return [
      { name: 'Price', value: price || 0 },
      { name: 'Description', value: desc || 0 },
      { name: 'Image', value: img || 0 },
      { name: 'Errors', value: err || 0 },
    ].filter(d => d.value > 0);
  }, [summary, items]);

  // ------ Permission Check ------

  if (permissionsLoading) {
    return <DashboardSkeleton />;
  }

  // Staff reach a page only through its tick (PageGate), so a ticked page is theirs to use
  const hasAccess = role === 'owner' || role === 'admin' || role === 'staff';
  if (!hasAccess) {
    return (
      <Box sx={{ p: 3, bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
        <Title title={`${stagingOrgLabel()} Item Staging`} />
        <Alert severity="error" sx={{ bgcolor: alpha(colors.error, 0.1), color: colors.error, border: `1px solid ${alpha(colors.error, 0.3)}` }}>
          Access Denied: This page is only accessible by owners and administrators.
        </Alert>
      </Box>
    );
  }

  // ======================================================================
  // RENDER
  // ======================================================================

  return (
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
      <Title title={`${stagingOrgLabel()} Item Staging`} />

      {/* ============================================================ */}
      {/* Glassmorphism Header */}
      {/* ============================================================ */}
      <Box sx={{ ...glassHeaderStyle, px: 3, py: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography
                variant="h5"
                sx={{
                  color: colors.textPrimary,
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                }}
              >
                {stagingOrgLabel()} Item Staging
              </Typography>
              {/* Live indicator */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: alpha(colors.success, 0.1),
                px: 1,
                py: 0.25,
                borderRadius: 10,
                border: `1px solid ${alpha(colors.success, 0.2)}`,
              }}>
                <Box sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: colors.success,
                  animation: 'skc-live-dot 2s ease-in-out infinite',
                  boxShadow: `0 0 6px ${colors.success}`,
                }} />
                <Typography variant="caption" sx={{ color: colors.success, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em' }}>
                  LIVE
                </Typography>
              </Box>
            </Stack>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 0.25 }}>
              Manage staged menu items and approve changes
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {lastUpdate && (
              <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                {lastUpdate.toLocaleTimeString()}
              </Typography>
            )}
            <Tooltip title="Refresh all data">
              <IconButton
                onClick={refreshAll}
                sx={{
                  color: colors.textSecondary,
                  bgcolor: colors.bgElevated,
                  border: `1px solid ${colors.border}`,
                  transition: 'all 0.2s',
                  '&:hover': {
                    color: colors.info,
                    borderColor: alpha(colors.info, 0.4),
                    boxShadow: `0 0 15px ${alpha(colors.info, 0.2)}`,
                    transform: 'rotate(180deg)',
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: 3, pb: 4 }}>
        {/* ============================================================ */}
        {/* Summary Cards */}
        {/* ============================================================ */}
        {summaryLoading ? (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {[1, 2, 3, 4].map((i) => (
              <Grid size={{ xs: 6, md: 3 }} key={i}>
                <Skeleton variant="rounded" height={100} sx={{ bgcolor: colors.bgCard, borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        ) : summary ? (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {(() => {
              const cards: Array<{ key: string; filterValue: string; label: string; subtitle: string; icon: React.ReactElement; color: string; count: number }> = [
                { key: 'new_item', filterValue: 'new_item', label: 'New Items', subtitle: 'Products not yet in catalogue', icon: <NewItemIcon />, color: colors.info, count: summary.pendingNewItems ?? 0 },
                { key: 'name', filterValue: 'name', label: 'Name Changes', subtitle: 'Product name updates pending', icon: <EditIcon />, color: colors.warning, count: summary.fieldChangeCounts?.name ?? 0 },
                { key: 'description', filterValue: 'description', label: 'Description Changes', subtitle: 'Product description edits', icon: <DescriptionIcon />, color: colors.purple, count: summary.fieldChangeCounts?.description ?? 0 },
                { key: 'image_url', filterValue: 'image_url', label: 'Image Changes', subtitle: 'Product image replacements', icon: <ImageIcon />, color: colors.warning, count: summary.fieldChangeCounts?.image_url ?? 0 },
              ];
              return cards.map((card) => (
                <Grid size={{ xs: 6, md: 3 }} key={card.key}>
                  <GlowCard
                    label={card.label}
                    value={card.count}
                    color={card.color}
                    icon={card.icon}
                    subtitle={card.subtitle}
                    pulse={card.count > 0}
                    active={changeTypeFilter === card.filterValue}
                    onClick={() => {
                      const next = changeTypeFilter === card.filterValue ? '' : card.filterValue;
                      setChangeTypeFilter(next);
                      setItemsPage(1);
                    }}
                  />
                </Grid>
              ));
            })()}
          </Grid>
        ) : null}

        {/* ============================================================ */}
        {/* Processing Progress Panel (live Socket.IO updates) */}
        {/* ============================================================ */}
        <VaperiteProcessingProgressPanel {...processingProgress} />

        {/* ============================================================ */}
        {/* Tabs */}
        {/* ============================================================ */}
        <Box sx={{
          mt: 3,
          borderBottom: `1px solid ${colors.border}`,
        }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              '& .MuiTab-root': {
                color: colors.textMuted,
                fontWeight: 600,
                fontSize: '0.8rem',
                letterSpacing: '0.02em',
                textTransform: 'none',
                transition: 'all 0.2s',
                '&:hover': { color: colors.textSecondary },
                '&.Mui-selected': { color: colors.info },
              },
              '& .MuiTabs-indicator': {
                bgcolor: colors.info,
                height: 3,
                borderRadius: '3px 3px 0 0',
                boxShadow: `0 0 10px ${alpha(colors.info, 0.5)}`,
              },
            }}
          >
            <Tab
              icon={<InventoryIcon sx={{ fontSize: 18 }} />}
              label={`Pending Review${summary ? ` (${itemsTotal})` : ''}`}
              iconPosition="start"
            />
            <Tab
              icon={<TuneIcon sx={{ fontSize: 18 }} />}
              label="Processing Rules"
              iconPosition="start"
            />
            <Tab
              icon={<RuleIcon sx={{ fontSize: 18 }} />}
              label="Missing Value Rules"
              iconPosition="start"
            />
            <Tab
              icon={<CategoryIcon sx={{ fontSize: 18 }} />}
              label="Category Mappings"
              iconPosition="start"
            />
            <Tab
              icon={<BrandIcon sx={{ fontSize: 18 }} />}
              label="Brand Mappings"
              iconPosition="start"
            />
            <Tab
              icon={<LocalOfferIcon sx={{ fontSize: 18 }} />}
              label={stagingOrg() === 'deeliver' ? 'Tags' : 'Tags & Name Rules'}
              iconPosition="start"
            />
            <Tab
              icon={<FlagIcon sx={{ fontSize: 18 }} />}
              label={`Flagged Items${summary ? ` (${summary.flagged ?? 0})` : ''}`}
              iconPosition="start"
            />
            <Tab
              icon={<BlockIcon sx={{ fontSize: 18 }} />}
              label={`Excluded Products${summary ? ` (${summary.excluded ?? 0})` : ''}`}
              iconPosition="start"
            />
            <Tab
              icon={<VisibilityOffIcon sx={{ fontSize: 18 }} />}
              label={`Live: still missing${liveMissingTotal !== null ? ` (${liveMissingTotal})` : ""}`}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* ============================================================ */}
        {/* Tab 0: Staged Items */}
        {/* ============================================================ */}
        <TabPanel value={activeTab} index={0}>
          {/* Review Pipeline Health */}
          <SystemHealthPanel metrics={metrics} variant="items" />

          {/* Filters Bar */}
          <Paper sx={{
            p: 2,
            mb: 2.5,
            bgcolor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}>
            <FormControl size="small" sx={{ minWidth: 140, ...elevatedInputStyle }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={itemsStatus}
                label="Status"
                onChange={(e) => { setItemsStatus(e.target.value); setItemsPage(1); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>

            {summary?.uniqueStores && summary.uniqueStores.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 160, ...elevatedInputStyle }}>
                <InputLabel>Store</InputLabel>
                <Select
                  value={storeFilter}
                  label="Store"
                  onChange={(e) => { setStoreFilter(e.target.value); setItemsPage(1); }}
                >
                  <MenuItem value="">All Stores</MenuItem>
                  {summary.uniqueStores.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              size="small"
              placeholder="Search by SKU or name..."
              value={itemsSearch}
              onChange={(e) => setItemsSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchItems()}
              sx={{ minWidth: 280, ...elevatedInputStyle }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors.textMuted, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 160, ...elevatedInputStyle }}>
              <InputLabel>Change Type</InputLabel>
              <Select
                value={changeTypeFilter}
                label="Change Type"
                onChange={(e) => { setChangeTypeFilter(e.target.value); setItemsPage(1); }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="new_item">New Item</MenuItem>
                {rules
                  .filter(r => r.requiresApproval && !['new_item', 'new_store', 'stock'].includes(r.fieldName))
                  .map((r) => (
                    <MenuItem key={r.fieldName} value={r.fieldName}>
                      {r.fieldName.replace(/_/g, ' ').replace(/^\w/, (c: string) => c.toUpperCase())} Change
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <PendingReviewFilters
              value={ruleFilters}
              onChange={(next) => { setRuleFilters(next); setItemsPage(1); }}
            />

            <Button
              variant="outlined"
              size="small"
              onClick={fetchItems}
              sx={{
                borderColor: colors.border,
                color: colors.textSecondary,
                '&:hover': {
                  borderColor: colors.info,
                  color: colors.info,
                  bgcolor: alpha(colors.info, 0.05),
                },
              }}
            >
              Search
            </Button>

            {itemsTotal > 0 && (
              <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                Showing {formatNumber(items.length)} of {formatNumber(itemsTotal)} items
              </Typography>
            )}

            <Box sx={{ flex: 1 }} />

            {/* Discrepancy mini-chart */}
            {discrepancyPieData.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 40, height: 40 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={discrepancyPieData} dataKey="value" cx="50%" cy="50%" innerRadius={10} outerRadius={18} strokeWidth={0}>
                        {discrepancyPieData.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>
                  Discrepancies
                </Typography>
              </Box>
            )}

            <PendingReviewColumnsButton columns={pendingColumns} />
            <Tooltip
              title={ruleFilterActive
                ? `Approve All ignores filters and approves the whole queue. Clear "Unmapped type" / "${stagingOrg() === 'deeliver' ? 'Hidden (hide-in-app)' : `No ${VAPERITE_SELL_IN_APP_TAG} tag`}" first, or select rows and use Approve Selected.`
                : 'Approves every pending item and field change, ignoring filters.'}
            >
              {/* A disabled button fires no events: the span carries the tooltip. */}
              <span data-testid="approve-all-wrapper">
                <Button
                  variant="outlined"
                  size="small"
                  disabled={approveAllLoading || ruleFilterActive}
                  onClick={handleApproveAll}
                  sx={{
                    borderColor: alpha(colors.success, 0.5),
                    color: colors.success,
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    '&:hover': {
                      borderColor: colors.success,
                      bgcolor: alpha(colors.success, 0.05),
                    },
                  }}
                >
                  {approveAllLoading ? 'Approving...' : 'Approve All'}
                </Button>
              </span>
            </Tooltip>

            {selectedSkus.size > 0 && (
              <Button
                variant="contained"
                size="small"
                startIcon={<ApproveIcon />}
                onClick={() => requestApprove(Array.from(selectedSkus), true)}
                sx={{
                  bgcolor: colors.success,
                  color: colors.bgDeepest,
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: colors.success,
                    boxShadow: `0 0 20px ${alpha(colors.success, 0.4)}`,
                  },
                }}
              >
                Approve {selectedSkus.size} Selected
              </Button>
            )}

            {selectedSkus.size > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RejectIcon />}
                onClick={() => setBulkRejectOpen(true)}
                sx={{
                  borderColor: alpha(colors.error, 0.5),
                  color: colors.error,
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: colors.error,
                    bgcolor: alpha(colors.error, 0.05),
                    boxShadow: `0 0 20px ${alpha(colors.error, 0.2)}`,
                  },
                }}
              >
                Reject {selectedSkus.size}
              </Button>
            )}
          </Paper>

          {/* Items Table */}
          {itemsLoading ? (
            <Box>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} variant="rounded" height={48} sx={{ bgcolor: colors.bgCard, mb: 0.5, borderRadius: 1 }} />
              ))}
            </Box>
          ) : items.length === 0 ? (
            <Paper sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
            }}>
              <InventoryIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 1 }} />
              <Typography variant="body1" sx={{ color: colors.textSecondary }}>
                No staged items found matching your filters.
              </Typography>
            </Paper>
          ) : (
            <>
              {/* The table owns its scrolling so the header row can stay frozen
                  (`stickyHeader` pins to the scroll container, not the window). */}
              <TableContainer
                ref={itemsTableRef}
                component={Paper}
                sx={{
                  bgcolor: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 2,
                  maxHeight: 'calc(100vh - 300px)',
                  minHeight: 320,
                  ...darkTableSx,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {itemsStatus === 'pending' && (
                        <TableCell padding="checkbox" sx={{ width: 48 }}>
                          <Checkbox
                            checked={pendingItems.length > 0 && selectedSkus.size === pendingItems.length}
                            indeterminate={selectedSkus.size > 0 && selectedSkus.size < pendingItems.length}
                            onChange={toggleSelectAll}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ width: 80 }}>Type</TableCell>
                      <SortableHeader field="sku" label="SKU" currentSort={sortField} currentDir={sortDir} onSort={handleSort} />
                      <SortableHeader field="suggestedName" label="Name" currentSort={sortField} currentDir={sortDir} onSort={handleSort} />
                      {STAGED_ITEM_RULE_HEADERS.filter((h) => pendingColumns.visible.has(h)).map((h) => (
                        RULE_SORT_FIELDS[h]
                          ? <SortableHeader key={h} field={RULE_SORT_FIELDS[h]!} label={h} currentSort={sortField} currentDir={sortDir} onSort={handleSort} />
                          : <TableCell key={h} align={h === 'Sell in app' || h === '18+' ? 'center' : undefined}>{h}</TableCell>
                      ))}
                      <SortableHeader field="basePrice" label="Price" currentSort={sortField} currentDir={sortDir} onSort={handleSort} />
                      <SortableHeader field="storeCount" label="Stores" currentSort={sortField} currentDir={sortDir} onSort={handleSort} />
                      <TableCell>Status</TableCell>
                      <TableCell>Changed Fields</TableCell>
                      <SortableHeader field="stagedAt" label="Staged" currentSort={sortField} currentDir={sortDir} onSort={handleSort} />
                      <TableCell align="right" sx={STICKY_ACTIONS_HEAD_SX}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedItems.map((item) => (
                      <TableRow
                        key={item.sku}
                        onClick={() => handleViewDetail(item.sku)}
                        sx={{
                          cursor: 'pointer',
                          ...(item.hasCriticalErrors ? {
                            animation: 'skc-error-pulse 3s ease-in-out infinite',
                            borderLeft: `3px solid ${colors.error}`,
                          } : {}),
                        }}
                      >
                        {itemsStatus === 'pending' && (
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            {item.status === 'pending' && (
                              <Checkbox
                                checked={selectedSkus.has(item.sku)}
                                onChange={() => toggleSelect(item.sku)}
                              />
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          {(() => {
                            const typeMap = {
                              new_item: { label: 'NEW', color: colors.info },
                              field_update: { label: 'UPDATE', color: colors.warning },
                              removal: { label: 'REMOVAL', color: colors.error },
                            };
                            const t = typeMap[item.type] || typeMap.new_item;
                            return (
                              <Chip
                                label={t.label}
                                size="small"
                                sx={{
                                  bgcolor: alpha(t.color, 0.12),
                                  color: t.color,
                                  fontWeight: 700,
                                  fontSize: '0.6rem',
                                  letterSpacing: '0.05em',
                                  border: `1px solid ${alpha(t.color, 0.3)}`,
                                  height: 22,
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                              fontWeight: 700,
                              color: colors.info,
                              fontSize: '0.8rem',
                            }}
                          >
                            {item.sku}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 220,
                                color: colors.textPrimary,
                                fontWeight: 500,
                              }}
                              noWrap
                              title={item.suggestedName}
                            >
                              {item.suggestedName}
                            </Typography>
                            {holdsUnmapped && item.type === 'new_item' && item.unmappedCategory && (
                              <Tooltip title="Held: this Vend category isn't mapped. Click to map it or create the category.">
                                <Chip
                                  label={`Unmapped: ${item.unmappedCategory}`}
                                  size="small"
                                  onClick={() => setResolveVendCategory(item.unmappedCategory ?? null)}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.62rem',
                                    fontWeight: 600,
                                    bgcolor: alpha(colors.warning, 0.12),
                                    color: colors.warning,
                                    border: `1px solid ${alpha(colors.warning, 0.3)}`,
                                    cursor: 'pointer',
                                  }}
                                />
                              </Tooltip>
                            )}
                            {item.suggestedImageUrl && (
                              <Tooltip title="Has image">
                                <ImageIcon sx={{ fontSize: 16, color: colors.purple, opacity: 0.7 }} />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <StagedItemRuleCells item={item} visible={pendingColumns.visible} />
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                            {formatPrice(item.basePrice)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={<StoreIcon sx={{ fontSize: '14px !important' }} />}
                            label={item.storeCount || 0}
                            size="small"
                            sx={{
                              bgcolor: alpha(colors.info, 0.1),
                              color: colors.info,
                              border: `1px solid ${alpha(colors.info, 0.2)}`,
                              height: 24,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <StatusChip status={item.status} />
                        </TableCell>
                        <TableCell>
                          {item.changedFields && item.changedFields.length > 0 ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {item.changedFields.map((f: string) => {
                                const fieldColors: Record<string, string> = {
                                  name: colors.info,
                                  description: colors.purple,
                                  image_url: colors.warning,
                                  price: colors.success,
                                  brand: colors.purple,
                                };
                                const c = fieldColors[f] || colors.textMuted;
                                return (
                                  <Chip
                                    key={f}
                                    label={f.replace(/_/g, ' ').replace(/^\w/, (ch: string) => ch.toUpperCase())}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(c, 0.12),
                                      color: c,
                                      fontWeight: 600,
                                      fontSize: '0.6rem',
                                      height: 22,
                                      border: `1px solid ${alpha(c, 0.25)}`,
                                    }}
                                  />
                                );
                              })}
                            </Stack>
                          ) : (
                            <DiscrepancyChips item={item} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: colors.textMuted }}>
                            {formatTimeAgo(item.stagedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={STICKY_ACTIONS_CELL_SX}>
                          {item.status === 'pending' && (
                            <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                              <Tooltip title="Approve">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); requestApprove([item.sku], false); }}
                                  sx={{
                                    color: colors.textMuted,
                                    '&:hover': { color: colors.success, bgcolor: alpha(colors.success, 0.1) },
                                  }}
                                >
                                  <ApproveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); handleOpenEdit(item); }}
                                  sx={{
                                    color: colors.textMuted,
                                    '&:hover': { color: colors.info, bgcolor: alpha(colors.info, 0.1) },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {/* A field-change-only row (id 0: no staged row, just changes to a live
                                  product) has nothing for Reject to act on. Its changes are approved,
                                  kept or taken in the detail view. */}
                              {!(item.type === 'field_update' && !item.id) && (
                                <Tooltip title="Reject">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); setRejectSku(item.sku); }}
                                    sx={{
                                      color: colors.textMuted,
                                      '&:hover': { color: colors.error, bgcolor: alpha(colors.error, 0.1) },
                                    }}
                                  >
                                    <RejectIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
                  <Pagination
                    count={totalPages}
                    page={itemsPage}
                    onChange={(_, p) => {
                      setItemsPage(p);
                      // Page 2 used to render underneath you at whatever depth you
                      // had scrolled to. Reset the table's own scroll AND bring the
                      // table back into view (below the fixed app bar, which a bare
                      // scrollIntoView would tuck the header row under).
                      itemsTableRef.current?.scrollTo({ top: 0 });
                      scrollToListTop(itemsTableRef.current);
                    }}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: colors.textSecondary,
                        borderColor: colors.border,
                        '&.Mui-selected': {
                          bgcolor: alpha(colors.info, 0.15),
                          color: colors.info,
                          borderColor: alpha(colors.info, 0.3),
                        },
                        '&:hover': {
                          bgcolor: colors.bgElevated,
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </TabPanel>

        {/* ============================================================ */}
        {/* Tab 1: Processing Rules */}
        {/* ============================================================ */}
        <TabPanel value={activeTab} index={1}>
          <Paper sx={{
            bgcolor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
            overflow: 'hidden',
          }}>
            <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}` }}>
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
                Change Classification Rules
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                Fields marked as &quot;Requires Approval&quot; will be staged for admin review instead of being processed immediately.
              </Typography>
            </Box>
            {rulesLoading ? (
              <Box sx={{ p: 3 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rounded" height={48} sx={{ bgcolor: colors.bgElevated, mb: 0.5 }} />
                ))}
              </Box>
            ) : rules.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  No processing rules found. Run the staging migration to seed default rules.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={darkTableSx}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Behavior</TableCell>
                      <TableCell>Pending</TableCell>
                      <TableCell>Last Updated</TableCell>
                      <TableCell align="right">Toggle</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rules
                      // "New store" was retired (1 Nov 2026): every sync runs for a store we already have,
                      // so it never fired. Its old DB row is hidden for both Deeliver and Vaperite.
                      .filter((rule) => rule.fieldName !== 'new_store')
                      .map((rule) => {
                      const pendingCount = summary?.fieldChangeCounts?.[rule.fieldName] ?? 0;
                      return (
                      <React.Fragment key={rule.fieldName}>
                      <TableRow>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                            {rule.fieldName.replace(/_/g, ' ')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const m = rule.mode || (rule.requiresApproval ? 'stage' : 'immediate');
                            const label = m === 'stage' ? 'STAGED' : m === 'immediate' ? 'IMMEDIATE' : 'NOT MONITORED';
                            const chipColor = m === 'stage'
                              ? colors.warning
                              : m === 'immediate' ? colors.success : colors.textMuted;
                            return (
                              <Chip
                                label={label}
                                size="small"
                                sx={{
                                  bgcolor: alpha(chipColor, 0.12),
                                  color: chipColor,
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                  border: `1px solid ${alpha(chipColor, 0.3)}`,
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {pendingCount > 0 ? (
                            <Chip
                              label={pendingCount}
                              size="small"
                              onClick={() => {
                                setChangeTypeFilter(rule.fieldName);
                                setItemsStatus('');
                                setItemsPage(1);
                                setActiveTab(0);
                              }}
                              sx={{
                                bgcolor: alpha(colors.info, 0.12),
                                color: colors.info,
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                minWidth: 32,
                                cursor: 'pointer',
                                border: `1px solid ${alpha(colors.info, 0.3)}`,
                                '&:hover': {
                                  bgcolor: alpha(colors.info, 0.25),
                                },
                              }}
                            />
                          ) : (
                            <Typography variant="caption" sx={{ color: colors.textMuted }}>
                              0
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: colors.textMuted }}>
                            {formatTimeAgo(rule.updatedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {(['stage', 'immediate', 'ignore'] as const).map((m) => {
                              const currentMode = rule.mode || (rule.requiresApproval ? 'stage' : 'immediate');
                              const isActive = currentMode === m;
                              const labels = { stage: 'Stage', immediate: 'Immediate', ignore: "Don't Monitor" };
                              return (
                                <Button
                                  key={m}
                                  size="small"
                                  variant={isActive ? 'contained' : 'outlined'}
                                  disabled={isActive}
                                  onClick={() => handleSetRuleMode(rule.fieldName, m)}
                                  sx={{
                                    minWidth: 0,
                                    px: 1,
                                    py: 0.25,
                                    fontSize: '0.62rem',
                                    fontWeight: isActive ? 700 : 500,
                                    textTransform: 'none',
                                    borderColor: colors.border,
                                    color: isActive ? colors.bgDeepest : colors.textMuted,
                                    bgcolor: isActive
                                      ? (m === 'stage' ? colors.warning : m === 'immediate' ? colors.success : colors.textMuted)
                                      : 'transparent',
                                    '&:hover': {
                                      borderColor: colors.info,
                                      color: colors.info,
                                      bgcolor: alpha(colors.info, 0.05),
                                    },
                                    '&.Mui-disabled': {
                                      color: colors.bgDeepest,
                                      bgcolor: m === 'stage' ? colors.warning : m === 'immediate' ? colors.success : colors.textMuted,
                                      opacity: 1,
                                    },
                                  }}
                                >
                                  {labels[m]}
                                </Button>
                              );
                            })}
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {rule.fieldName === 'category' && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 1, pl: 6, borderBottom: `1px solid ${colors.border}` }}>
                            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600 }}>
                                When the category isn't mapped:
                              </Typography>
                              {([false, true] as const).map((hold) => {
                                const isActive = (rule.options?.holdUnmapped ?? false) === hold;
                                return (
                                  <Button
                                    key={String(hold)}
                                    size="small"
                                    variant="outlined"
                                    disabled={isActive}
                                    onClick={async () => {
                                      try {
                                        await authInstance.put(
                                          `${stagingApiBase()}/staging/rules/${rule.fieldName}/options`,
                                          { ...rule.options, holdUnmapped: hold },
                                        );
                                        notify(
                                          hold
                                            ? 'Unmapped categories now hold new items for a decision'
                                            : 'Unmapped categories now use the Default Category',
                                          { type: 'success' },
                                        );
                                        fetchRules();
                                        refreshAll();
                                      } catch (err: any) {
                                        notify(`Failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
                                      }
                                    }}
                                    sx={{
                                      minWidth: 0,
                                      px: 1,
                                      py: 0.25,
                                      fontSize: '0.62rem',
                                      fontWeight: isActive ? 700 : 500,
                                      textTransform: 'none',
                                      borderColor: colors.border,
                                      color: isActive ? colors.bgDeepest : colors.textMuted,
                                      '&.Mui-disabled': {
                                        color: colors.bgDeepest,
                                        bgcolor: hold ? colors.warning : colors.textMuted,
                                        opacity: 1,
                                      },
                                    }}
                                  >
                                    {hold ? 'Stage until mapped' : 'Use fallback'}
                                  </Button>
                                );
                              })}
                              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                                {rule.options?.holdUnmapped
                                  ? 'New items wait until you map the Vend category or create a category for it.'
                                  : 'New items go to the Default Category (Category Mappings), else General.'}
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                      {rule.fieldName === 'image_url' && (rule.mode || (rule.requiresApproval ? 'stage' : 'immediate')) === 'immediate' && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 1, pl: 6, borderBottom: `1px solid ${colors.border}` }}>
                            <Stack direction="row" spacing={3} alignItems="center">
                              <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600 }}>
                                Options:
                              </Typography>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={rule.options?.smartTrim ?? false}
                                    onChange={async (e) => {
                                      const newOptions = { ...rule.options, smartTrim: e.target.checked };
                                      try {
                                        await authInstance.put(
                                          `${stagingApiBase()}/staging/rules/${rule.fieldName}/options`,
                                          newOptions,
                                        );
                                        notify(`Smart trim ${e.target.checked ? 'enabled' : 'disabled'}`, { type: 'success' });
                                        fetchRules();
                                      } catch (err: any) {
                                        notify(`Failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
                                      }
                                    }}
                                  />
                                }
                                label={
                                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                                    Smart Trim
                                  </Typography>
                                }
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={rule.options?.imageHash ?? true}
                                    onChange={async (e) => {
                                      const newOptions = { ...rule.options, imageHash: e.target.checked };
                                      try {
                                        await authInstance.put(
                                          `${stagingApiBase()}/staging/rules/${rule.fieldName}/options`,
                                          newOptions,
                                        );
                                        notify(`Image hash ${e.target.checked ? 'enabled' : 'disabled'}`, { type: 'success' });
                                        fetchRules();
                                      } catch (err: any) {
                                        notify(`Failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
                                      }
                                    }}
                                  />
                                }
                                label={
                                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                                    Image Hash Comparison
                                  </Typography>
                                }
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                      <RuleOptionRows rule={rule} onChanged={fetchRules} />
                      </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </TabPanel>

        {/* ============================================================ */}
        {/* Tab 2: Missing Value Rules */}
        {/* ============================================================ */}
        <TabPanel value={activeTab} index={2}>
          <MissingValueRulesTab onChanged={() => { fetchSummary(); fetchItems(); }} />
        </TabPanel>

        {/* ============================================================ */}
        {/* Tab 3: Category Mappings */}
        {/* ============================================================ */}
        <TabPanel value={activeTab} index={3}>
          <VaperiteCategoryMappings />
        </TabPanel>

        {/* ============================================================ */}
        {/* Tab 4: Brand Mappings (Jason, 29 Oct 2026) */}
        {/* ============================================================ */}
        <TabPanel value={activeTab} index={4}>
          <BrandMappingsTab onChanged={() => { fetchSummary(); fetchItems(); }} />
        </TabPanel>

        {/* ============================================================ */}
        {/* Tab 5: Tags (roles + name rules) */}
        {/* ============================================================ */}
        <TabPanel value={activeTab} index={5}>
          <TagsTab />
        </TabPanel>

        {/* ============================================================ */}
        {/* Tab 6: Flagged Items */}
        {/* ============================================================ */}
        <TabPanel value={activeTab} index={6}>
          <FlaggedItemsTab onChanged={() => { fetchSummary(); fetchItems(); }} />
        </TabPanel>

        {/* ============================================================ */}
        {/* Tab 7: Excluded Products */}
        {/* ============================================================ */}
        <TabPanel value={activeTab} index={7}>
          <ExcludedItemsTab onChanged={() => { fetchSummary(); fetchItems(); }} />
        </TabPanel>

        {/* ============================================================ */}
        {/* Tab 8: Live products still missing a value (hidden by a rule, or no description) */}
        {/* ============================================================ */}
        <TabPanel value={activeTab} index={8}>
          <LiveMissingTab onChanged={fetchSummary} />
        </TabPanel>
      </Box>

      <NoWoocommerceDialog
        open={!!noWoo}
        items={noWoo?.items ?? []}
        total={noWoo?.total}
        busy={noWooBusy}
        onConfirm={noWooConfirm}
        // Approve All only has a count: exclude those from Pending Review's filter instead.
        onExclude={noWoo && noWoo.items.length > 0 ? noWooExclude : undefined}
        onSkip={() => {
          if (noWoo?.kind === 'all') {
            setRuleFilters((f) => ({ ...f, sellInApp: false }));
            setItemsPage(1);
          }
          setNoWoo(null);
        }}
      />

      {/* ============================================================ */}
      {/* Reject Dialog */}
      {/* ============================================================ */}
      <Dialog
        open={!!rejectSku}
        onClose={() => setRejectSku(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            boxShadow: `0 0 40px ${alpha(colors.error, 0.15)}`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RejectIcon sx={{ color: colors.error }} />
            <span>Reject {rejectSku}</span>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
            Provide a reason for rejecting <strong style={{ color: colors.textPrimary }}>{rejectSku}</strong>.
          </Typography>
          <TextField
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            required
            multiline
            rows={2}
            error={!rejectReason.trim()}
            helperText={!rejectReason.trim() ? 'Reason is required' : ''}
            sx={elevatedInputStyle}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button onClick={() => setRejectSku(null)} sx={{ color: colors.textSecondary }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleReject}
            disabled={!rejectReason.trim()}
            sx={{
              bgcolor: colors.error,
              color: colors.bgDeepest,
              fontWeight: 700,
              '&:hover': {
                bgcolor: colors.error,
                boxShadow: `0 0 20px ${alpha(colors.error, 0.4)}`,
              },
            }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* Bulk Approve Dialog */}
      {/* ============================================================ */}
      <Dialog
        open={approvePlan != null}
        onClose={() => setApprovePlan(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            boxShadow: `0 0 40px ${alpha(colors.success, 0.15)}`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ApproveIcon sx={{ color: colors.success }} />
            <span>
              {approvePlan?.skus.length === 1
                ? `Approve ${approvePlan.skus[0]}`
                : `Approve ${approvePlan?.skus.length ?? 0} items`}
            </span>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
            Tick what you are approving. Anything left unticked stays pending.
          </Typography>
          <Stack sx={{ mb: 2 }}>
            {approvePlan?.plan.options.map((opt) => (
              <FormControlLabel
                key={opt.key}
                control={
                  <Checkbox
                    size="small"
                    checked={approveChoice.has(opt.key)}
                    onChange={(e) => setApproveChoice((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(opt.key); else next.delete(opt.key);
                      return next;
                    })}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                    {opt.label}
                    {(approvePlan?.skus.length ?? 0) > 1 && (
                      <Typography component="span" variant="caption" sx={{ color: colors.textMuted, ml: 1 }}>
                        × {opt.count}
                      </Typography>
                    )}
                  </Typography>
                }
              />
            ))}
          </Stack>
          {(approvePlan?.plan.missing.length ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {approvePlan!.plan.missing.length === 1 ? 'This new item would go live without:' : `${approvePlan!.plan.missing.length} new items would go live with values missing:`}
              </Typography>
              {approvePlan!.plan.missing.slice(0, 8).map((m) => (
                <Typography key={m.sku} variant="caption" component="div">
                  {m.sku} {m.name} — no {m.fields.join(', ')}
                </Typography>
              ))}
              {approvePlan!.plan.missing.length > 8 && (
                <Typography variant="caption" component="div">and {approvePlan!.plan.missing.length - 8} more</Typography>
              )}
            </Alert>
          )}
          {approveChoice.has(NEW_ITEM) && (
            <TextField
              label="Category Override (optional)"
              value={bulkApproveCategory}
              onChange={(e) => setBulkApproveCategory(e.target.value)}
              fullWidth
              size="small"
              sx={elevatedInputStyle}
              helperText="New items only: override the category resolved from the mappings"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button onClick={() => { setApprovePlan(null); setBulkApproveCategory(''); }} sx={{ color: colors.textSecondary }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={approveChoice.size === 0}
            onClick={() => {
              if (!approvePlan) return;
              const only = approvePlan.skus.length === 1 ? items.find((i) => i.sku === approvePlan.skus[0]) : undefined;
              // One new item: the single-item approve, which handles the no-woocommerce and
              // unmapped-category answers with their own dialogs.
              if (only && only.type === 'new_item' && approveChoice.has(NEW_ITEM) && !bulkApproveCategory.trim()) {
                setApprovePlan(null);
                handleApprove(only.sku);
                return;
              }
              handleBulkApprove({ skus: approvePlan.skus, choice: approveChoice, bulk: approvePlan.bulk });
            }}
            sx={{
              bgcolor: colors.success,
              color: colors.bgDeepest,
              fontWeight: 700,
              '&:hover': { bgcolor: colors.success, boxShadow: `0 0 20px ${alpha(colors.success, 0.4)}` },
            }}
          >
            Approve {approveChoice.size === 0 ? '' : `(${approveChoice.size})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* Bulk Reject Dialog */}
      {/* ============================================================ */}
      <Dialog
        open={bulkRejectOpen}
        onClose={() => setBulkRejectOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            boxShadow: `0 0 40px ${alpha(colors.error, 0.15)}`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RejectIcon sx={{ color: colors.error }} />
            <span>Bulk Reject {selectedSkus.size} Items</span>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
            Reject <strong style={{ color: colors.textPrimary }}>{selectedSkus.size}</strong> selected items. This cannot be undone.
          </Typography>
          <TextField
            label="Reason"
            value={bulkRejectReason}
            onChange={(e) => setBulkRejectReason(e.target.value)}
            fullWidth
            required
            multiline
            rows={2}
            error={!bulkRejectReason.trim()}
            helperText={!bulkRejectReason.trim() ? 'Reason is required' : ''}
            sx={elevatedInputStyle}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button onClick={() => setBulkRejectOpen(false)} sx={{ color: colors.textSecondary }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkReject}
            disabled={!bulkRejectReason.trim()}
            sx={{
              bgcolor: colors.error,
              color: colors.bgDeepest,
              fontWeight: 700,
              '&:hover': { bgcolor: colors.error, boxShadow: `0 0 20px ${alpha(colors.error, 0.4)}` },
            }}
          >
            Reject All
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* Detail Dialog */}
      {/* ============================================================ */}
      <Dialog
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSkcTrimPreview(null); }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            boxShadow: `0 0 60px rgba(0,0,0,0.5)`,
          },
        }}
      >
        <DialogTitle sx={{
          color: colors.textPrimary,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          {detailItem ? (
            <>
              {(() => {
                const typeMap = {
                  new_item: { label: 'NEW', color: colors.info },
                  field_update: { label: 'UPDATE', color: colors.warning },
                  removal: { label: 'REMOVAL', color: colors.error },
                };
                const t = typeMap[detailItem.type] || typeMap.new_item;
                return (
                  <Chip
                    label={t.label}
                    size="small"
                    sx={{
                      bgcolor: alpha(t.color, 0.12),
                      color: t.color,
                      fontWeight: 700,
                      fontSize: '0.6rem',
                      border: `1px solid ${alpha(t.color, 0.3)}`,
                      height: 22,
                    }}
                  />
                );
              })()}
              <Typography
                component="span"
                sx={{
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  color: colors.info,
                  fontWeight: 700,
                }}
              >
                {detailItem.sku}
              </Typography>
              <Typography component="span" sx={{ color: colors.textMuted, mx: 1 }}>—</Typography>
              <Typography component="span" sx={{ fontWeight: 500 }}>
                {detailItem.suggestedName}
              </Typography>
            </>
          ) : 'Loading...'}
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: colors.info }} />
            </Box>
          ) : detailItem ? (
            <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
              {/* Image */}
              {detailItem.suggestedImageUrl && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={detailItem.suggestedImageUrl}
                      alt={detailItem.suggestedName}
                      sx={{
                        width: '100%',
                        maxHeight: 250,
                        objectFit: 'contain',
                        borderRadius: 2,
                        border: `1px solid ${colors.border}`,
                        bgcolor: colors.bgElevated,
                        p: 1,
                      }}
                      onError={(e: any) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <Box
                      sx={{
                        display: 'none',
                        width: '100%',
                        height: 200,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 1,
                        borderRadius: 2,
                        border: `1px solid ${colors.border}`,
                        bgcolor: colors.bgElevated,
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 48, color: colors.textMuted, opacity: 0.3 }} />
                      <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>
                        Image failed to load
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      component="a"
                      href={detailItem.suggestedImageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: colors.textMuted,
                        fontSize: '0.6rem',
                        display: 'block',
                        mt: 0.5,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textDecoration: 'none',
                        '&:hover': { color: colors.info, textDecoration: 'underline' },
                      }}
                    >
                      {detailItem.suggestedImageUrl}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => detailItem.suggestedImageUrl && handleSkcSmartTrimPreview(detailItem.suggestedImageUrl)}
                      disabled={skcTrimLoading}
                      startIcon={skcTrimLoading ? <CircularProgress size={14} /> : <CropIcon />}
                      sx={{ mt: 1, textTransform: 'none', fontSize: '0.7rem' }}
                    >
                      {skcTrimLoading ? 'Trimming...' : 'Smart Trim Preview'}
                    </Button>

                    {skcTrimPreview && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.6rem' }}>Before</Typography>
                          <Box
                            component="img"
                            src={skcTrimPreview.original}
                            alt="Before"
                            sx={{ display: 'block', maxWidth: 150, maxHeight: 150, borderRadius: 1, border: `1px solid ${colors.border}` }}
                          />
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ color: colors.success, fontSize: '0.6rem' }}>After Trim</Typography>
                          <Box
                            component="img"
                            src={skcTrimPreview.trimmed}
                            alt="After"
                            sx={{ display: 'block', maxWidth: 150, maxHeight: 150, borderRadius: 1, border: `2px solid ${colors.success}` }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Details */}
              <Grid size={{ xs: 12, md: detailItem.suggestedImageUrl ? 8 : 12 }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600 }}>
                      Name
                    </Typography>
                    <Typography variant="body1" sx={{ color: colors.textPrimary }}>
                      {detailItem.suggestedName}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={3}>
                    <Box>
                      <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600 }}>
                        Base Price
                      </Typography>
                      <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 700, fontFamily: 'monospace' }}>
                        {formatPrice(detailItem.basePrice)}
                      </Typography>
                      {/* A missing price means UNKNOWN, never free — surface the estimate
                          rather than let an item be approved onto the catalogue at R0.00. */}
                      {detailItem.status === 'pending'
                        && (detailItem.basePrice == null || detailItem.basePrice <= 0)
                        && suggestions?.basePrice && (
                        <Tooltip title={suggestions.basePrice.reason}>
                          <Chip
                            size="small"
                            label={`Suggested: R${suggestions.basePrice.value.toFixed(2)}`}
                            sx={{
                              mt: 0.5,
                              height: 20,
                              fontSize: '0.6rem',
                              color: colors.info,
                              bgcolor: alpha(colors.info, 0.1),
                              border: `1px dashed ${alpha(colors.info, 0.4)}`,
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600 }}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}><StatusChip status={detailItem.status} /></Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600 }}>
                        Discrepancies
                      </Typography>
                      <Box sx={{ mt: 0.5 }}><DiscrepancyChips item={detailItem} /></Box>
                    </Box>
                  </Stack>

                  {/*
                    Every field is rendered even when it has no value — a reviewer needs to
                    SEE that a product has no brand or no barcode, which a hidden field
                    cannot convey. Blanks show an em dash, plus the suggestion (if any)
                    derived from similar existing products. Suggestions are never applied
                    automatically: they are a starting point for Edit.
                  */}
                  <StagedFieldBlock
                    label="Description"
                    value={detailItem.suggestedDescription}
                    suggestion={detailItem.status === 'pending' ? suggestions?.description ?? null : null}
                  />
                  <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    <StagedFieldBlock
                      label="Category"
                      value={detailItem.categories?.map((c) => c.payloadCategoryName).filter(Boolean).join(', ')
                        || detailItem.categoryNames?.join(', ')}
                      suggestion={detailItem.status === 'pending' ? suggestions?.category ?? null : null}
                    />
                    <StagedFieldBlock
                      label="Tags"
                      value={detailItem.webhookTags}
                      suggestion={
                        detailItem.status === 'pending' && suggestions?.tags
                          ? { value: suggestions.tags.value.join(', '), reason: suggestions.tags.reason }
                          : null
                      }
                    />
                    <StagedFieldBlock
                      label="Brand"
                      value={detailItem.brand ?? (detailItem.canonicalItemData as { brand?: string } | undefined)?.brand}
                      suggestion={detailItem.status === 'pending' ? suggestions?.brand ?? null : null}
                    />
                    <StagedFieldBlock
                      label="Barcode"
                      value={detailItem.gtin}
                      suggestion={detailItem.status === 'pending' ? suggestions?.gtin ?? null : null}
                      mono
                    />
                  </Stack>
                  {detailItem.reviewedBy && (
                    <Box>
                      <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600 }}>
                        Reviewed By
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        {detailItem.reviewedBy} — {formatTimeAgo(detailItem.reviewedAt)}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Grid>

              {/* Store data */}
              {detailItem.stores && detailItem.stores.length > 0 && (
                <Grid size={12}>
                  <Box sx={{ borderTop: `1px solid ${colors.border}`, pt: 2, mt: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <StoreIcon sx={{ color: colors.info, fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
                        Store Data ({detailItem.stores.length})
                      </Typography>
                    </Stack>
                    <TableContainer sx={darkTableSx}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Store ID</TableCell>
                            <TableCell>Price</TableCell>
                            <TableCell>Stock Level</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detailItem.stores.map((s, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: colors.info }}>
                                  {s.externalStoreId}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {formatPrice(s.storePrice)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {s.suggestedStockLevel}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Grid>
              )}

              {/* Discrepancy Comparison */}
              {detailItem.discrepancyDetails && detailItem.discrepancyDetails.length > 0 && (
                <Grid size={12}>
                  <Box sx={{
                    borderTop: `1px solid ${colors.border}`,
                    pt: 2,
                    mt: 1,
                  }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <WarningIcon sx={{ color: colors.warning, fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ color: colors.warning, fontWeight: 700 }}>
                        Discrepancy Details
                      </Typography>
                    </Stack>
                    {detailItem.discrepancyDetails.map((disc, di) => (
                      <Box key={di} sx={{ mb: 2 }}>
                        <Chip
                          label={disc.field.replace(/_/g, ' ')}
                          size="small"
                          sx={{
                            bgcolor: alpha(colors.warning, 0.12),
                            color: colors.warning,
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            mb: 1,
                            textTransform: 'capitalize',
                            border: `1px solid ${alpha(colors.warning, 0.3)}`,
                          }}
                        />
                        <TableContainer sx={darkTableSx}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Store</TableCell>
                                <TableCell>Value</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {disc.stores.map((s, si) => (
                                <TableRow key={si}>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: colors.info }}>
                                      {s.externalStoreId}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {disc.field === 'image_url' && s.value ? (
                                      <Stack direction="row" alignItems="center" spacing={1}>
                                        <Box
                                          component="img"
                                          src={s.value}
                                          alt="Product"
                                          sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 1, bgcolor: colors.bgElevated }}
                                          onError={(e: any) => { e.target.style.display = 'none'; }}
                                        />
                                        <Typography variant="caption" sx={{ color: colors.textSecondary, fontFamily: 'monospace', maxWidth: 200 }} noWrap>
                                          {s.value}
                                        </Typography>
                                      </Stack>
                                    ) : disc.field === 'price' ? (
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                        {s.value ? formatPrice(parseFloat(s.value)) : '-'}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" sx={{ color: colors.textSecondary, maxWidth: 300 }} noWrap title={s.value || ''}>
                                        {s.value || '-'}
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Field Changes — NEW items get a clean initial-values layout */}
              {detailItem.fieldChanges && detailItem.fieldChanges.length > 0 && detailItem.type === 'new_item' && (
                <Grid size={12}>
                  <Box sx={{ borderTop: `1px solid ${colors.border}`, pt: 2, mt: 1 }}>
                    <Alert
                      severity="info"
                      icon={<NewItemIcon />}
                      sx={{
                        mb: 2,
                        bgcolor: alpha(colors.info, 0.08),
                        color: colors.textSecondary,
                        border: `1px solid ${alpha(colors.info, 0.2)}`,
                        '& .MuiAlert-icon': { color: colors.info },
                      }}
                    >
                      This is a new product — all fields below are initial values, not changes to an existing product.
                    </Alert>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <NewItemIcon sx={{ color: colors.info, fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
                        Initial Values
                      </Typography>
                    </Stack>
                    <TableContainer sx={{ ...darkTableSx, border: `1px solid ${colors.border}`, borderRadius: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Field</TableCell>
                            <TableCell>Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detailItem.fieldChanges.map((fc, idx) => {
                            const isImage = fc.fieldName === 'image_url';
                            const isPrice = fc.fieldName === 'price';
                            const fieldColors: Record<string, string> = {
                              name: colors.info,
                              description: colors.purple,
                              image_url: colors.warning,
                              price: colors.success,
                            };
                            const chipColor = fieldColors[fc.fieldName] || colors.textMuted;

                            return (
                              <TableRow key={`new-${idx}`}>
                                <TableCell>
                                  <Chip
                                    label={fc.fieldName.replace(/_/g, ' ').replace(/^\w/, (ch: string) => ch.toUpperCase())}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(chipColor, 0.12),
                                      color: chipColor,
                                      fontWeight: 600,
                                      fontSize: '0.65rem',
                                      border: `1px solid ${alpha(chipColor, 0.25)}`,
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {isImage && fc.newValue ? (
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Box
                                        component="img"
                                        src={fc.newValue}
                                        alt="Product"
                                        sx={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 1, bgcolor: colors.bgElevated, border: `1px solid ${alpha(colors.info, 0.3)}` }}
                                        onError={(e: any) => { e.target.style.display = 'none'; }}
                                      />
                                      <Typography
                                        variant="caption"
                                        component="a"
                                        href={fc.newValue}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                        sx={{ color: colors.textMuted, fontSize: '0.6rem', maxWidth: 120, textDecoration: 'none', '&:hover': { color: colors.info } }}
                                        noWrap
                                      >
                                        Open
                                      </Typography>
                                    </Stack>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: colors.textPrimary,
                                        maxWidth: 300,
                                      }}
                                      noWrap
                                      title={fc.newValue || ''}
                                    >
                                      {isPrice && fc.newValue ? formatPrice(parseFloat(fc.newValue)) : (fc.newValue || '—')}
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Grid>
              )}

              {/* Field Changes Diff View (field_update / removal types only) */}
              {detailItem.fieldChanges && detailItem.fieldChanges.length > 0 && detailItem.type !== 'new_item' && (
                <Grid size={12}>
                  <Box sx={{ borderTop: `1px solid ${colors.border}`, pt: 2, mt: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <EditIcon sx={{ color: colors.warning, fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
                          Changes
                        </Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: colors.textMuted }}>
                        {detailItem.fieldChanges.length} change{detailItem.fieldChanges.length !== 1 ? 's' : ''}
                      </Typography>
                    </Stack>
                    <TableContainer sx={{ ...darkTableSx, border: `1px solid ${colors.border}`, borderRadius: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox" sx={{ width: 48 }}>
                              {detailItem.type === 'field_update' && (
                                <Checkbox
                                  checked={
                                    detailItem.fieldChanges.filter(isBulkApprovable).length > 0 &&
                                    selectedFieldChangeIds.size === detailItem.fieldChanges.filter(isBulkApprovable).length
                                  }
                                  indeterminate={
                                    selectedFieldChangeIds.size > 0 &&
                                    selectedFieldChangeIds.size < detailItem.fieldChanges.filter(isBulkApprovable).length
                                  }
                                  onChange={() => {
                                    const allIds = detailItem.fieldChanges!
                                      .filter(isBulkApprovable)
                                      .map(fc => fc.id as number);
                                    if (selectedFieldChangeIds.size === allIds.length) {
                                      setSelectedFieldChangeIds(new Set());
                                    } else {
                                      setSelectedFieldChangeIds(new Set(allIds));
                                    }
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell>Field</TableCell>
                            <TableCell>Old Value</TableCell>
                            <TableCell sx={{ width: 30, textAlign: 'center' }}></TableCell>
                            <TableCell>New Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detailItem.fieldChanges.map((fc, idx) => {
                            const isImage = fc.fieldName === 'image_url';
                            const isPrice = fc.fieldName === 'price';
                            const fieldColors: Record<string, string> = {
                              name: colors.info,
                              description: colors.purple,
                              image_url: colors.warning,
                              price: colors.success,
                            };
                            const chipColor = fieldColors[fc.fieldName] || colors.textMuted;
                            const hasRealId = fc.id != null;
                            const isSelected = hasRealId && selectedFieldChangeIds.has(fc.id as number);

                            const icr = fc.imageCompareResult;
                            if (fc.overrideConflict) {
                              return (
                                <TableRow key={fc.id ?? `synth-${idx}`} data-testid={`override-conflict-${fc.fieldName}`}>
                                  <TableCell padding="checkbox" />
                                  <TableCell>
                                    <Stack spacing={0.5} alignItems="flex-start">
                                      <Chip
                                        label={fc.fieldName.replace(/_/g, ' ').replace(/^\w/, (ch: string) => ch.toUpperCase())}
                                        size="small"
                                        sx={{
                                          bgcolor: alpha(chipColor, 0.12),
                                          color: chipColor,
                                          fontWeight: 600,
                                          fontSize: '0.65rem',
                                          border: `1px solid ${alpha(chipColor, 0.25)}`,
                                        }}
                                      />
                                      <Typography variant="caption" sx={{ color: colors.warning, fontSize: '0.6rem', fontWeight: 700 }}>
                                        Overridden
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell colSpan={3}>
                                    <OverrideConflict
                                      change={fc}
                                      onResolved={(outcome) => {
                                        if (outcome === 'take') notify(`${fc.fieldName}: took Lightspeed's value`, { type: 'success' });
                                        if (outcome === 'keep') notify(`${fc.fieldName}: kept your value`, { type: 'success' });
                                        reloadDetail(detailItem.sku);
                                        refreshAll();
                                      }}
                                      onError={(message) => notify(message, { type: 'error' })}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            }
                            return (
                              <React.Fragment key={fc.id ?? `synth-${idx}`}>
                                <TableRow>
                                  <TableCell padding="checkbox">
                                    {detailItem.type === 'field_update' && hasRealId ? (
                                      <Checkbox
                                        checked={isSelected}
                                        onChange={() => {
                                          setSelectedFieldChangeIds(prev => {
                                            const next = new Set(prev);
                                            if (next.has(fc.id as number)) next.delete(fc.id as number);
                                            else next.add(fc.id as number);
                                            return next;
                                          });
                                        }}
                                      />
                                    ) : (
                                      <Checkbox checked disabled />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={fc.fieldName.replace(/_/g, ' ').replace(/^\w/, (ch: string) => ch.toUpperCase())}
                                      size="small"
                                      sx={{
                                        bgcolor: alpha(chipColor, 0.12),
                                        color: chipColor,
                                        fontWeight: 600,
                                        fontSize: '0.65rem',
                                        border: `1px solid ${alpha(chipColor, 0.25)}`,
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {isImage && fc.oldValue ? (
                                      <Stack direction="row" alignItems="center" spacing={1}>
                                        <Box
                                          component="img"
                                          src={fc.oldValue}
                                          alt="Old"
                                          sx={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 1, bgcolor: colors.bgElevated, opacity: 0.6, border: `1px solid ${alpha(colors.error, 0.3)}` }}
                                          onError={(e: any) => { e.target.style.display = 'none'; }}
                                        />
                                      </Stack>
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          textDecoration: fc.oldValue ? 'line-through' : 'none',
                                          color: fc.oldValue ? alpha(colors.error, 0.7) : colors.textMuted,
                                          maxWidth: 200,
                                          opacity: 0.8,
                                        }}
                                        noWrap
                                        title={fc.oldValue || ''}
                                      >
                                        {isPrice && fc.oldValue ? formatPrice(parseFloat(fc.oldValue)) : (fc.oldValue || '—')}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'center', color: colors.textMuted, fontSize: '1.1rem' }}>
                                    →
                                  </TableCell>
                                  <TableCell>
                                    {isImage && fc.newValue ? (
                                      <Stack direction="row" alignItems="center" spacing={1}>
                                        <Box
                                          component="img"
                                          src={fc.newValue}
                                          alt="New"
                                          sx={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 1, bgcolor: colors.bgElevated, border: `1px solid ${alpha(colors.success, 0.3)}` }}
                                          onError={(e: any) => { e.target.style.display = 'none'; }}
                                        />
                                        <Typography
                                          variant="caption"
                                          component="a"
                                          href={fc.newValue}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                          sx={{ color: colors.textMuted, fontSize: '0.6rem', maxWidth: 120, textDecoration: 'none', '&:hover': { color: colors.info } }}
                                          noWrap
                                        >
                                          Open
                                        </Typography>
                                      </Stack>
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          color: colors.success,
                                          maxWidth: 200,
                                        }}
                                        noWrap
                                        title={fc.newValue || ''}
                                      >
                                        {isPrice && fc.newValue ? formatPrice(parseFloat(fc.newValue)) : (fc.newValue || '—')}
                                      </Typography>
                                    )}
                                    {/* Their woocommerce tag was added: keep the product hidden (records an override). */}
                                    {fc.fieldName === 'sellable' && fc.id != null && !fc.overrideConflict && fc.newValue === 'true' && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.7rem', py: 0 }}
                                        onClick={(e) => { e.stopPropagation(); handleKeepHidden(fc.id!); }}
                                      >
                                        Keep hidden
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                                {/* Image hash debug row */}
                                {isImage && icr && (
                                  <TableRow>
                                    <TableCell colSpan={5} sx={{ py: 0.5, borderBottom: 'none' }}>
                                      <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 6 }}>
                                        <Chip
                                          size="small"
                                          label={`dHash: ${icr.result ?? 'pending'}`}
                                          sx={{
                                            fontSize: '0.6rem',
                                            height: 20,
                                            bgcolor: alpha(
                                              icr.result === 'different' ? colors.error
                                                : icr.result === 'auto_dismissed' ? colors.success
                                                : icr.result === 'skipped' ? colors.warning
                                                : colors.textMuted,
                                              0.12,
                                            ),
                                            color: icr.result === 'different' ? colors.error
                                              : icr.result === 'auto_dismissed' ? colors.success
                                              : icr.result === 'skipped' ? colors.warning
                                              : colors.textMuted,
                                            border: '1px solid',
                                            borderColor: 'currentColor',
                                          }}
                                        />
                                        {icr.distance != null && (
                                          <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.6rem', fontFamily: 'monospace' }}>
                                            distance: {icr.distance}/64
                                          </Typography>
                                        )}
                                        {icr.reason && (
                                          <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.6rem' }}>
                                            {icr.reason}
                                          </Typography>
                                        )}
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                )}
                                {isImage && !icr && (
                                  <TableRow>
                                    <TableCell colSpan={5} sx={{ py: 0.5, borderBottom: 'none' }}>
                                      <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.6rem', pl: 6, fontStyle: 'italic' }}>
                                        Image comparison not yet run
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Grid>
              )}

              {/* Categories (deduplicated by name) */}
              {detailItem.categories && detailItem.categories.length > 0 && (() => {
                const uniqueCats = Array.from(
                  new Map(detailItem.categories.map(c => [c.payloadCategoryName ?? c.payloadCategoryId, c])).values(),
                );
                return (
                  <Grid size={12}>
                    <Box sx={{ borderTop: `1px solid ${colors.border}`, pt: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <CategoryIcon sx={{ color: colors.purple, fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
                          Categories ({uniqueCats.length})
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {uniqueCats.map((c, i) => (
                          <Chip
                            key={i}
                            label={c.payloadCategoryName || c.payloadCategoryId || 'Unknown'}
                            size="small"
                            sx={{
                              bgcolor: alpha(colors.purple, 0.12),
                              color: colors.purple,
                              border: `1px solid ${alpha(colors.purple, 0.25)}`,
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Grid>
                );
              })()}

              {/* Raw Payload */}
              {detailItem.canonicalItemData && (
                <Grid size={12}>
                  <Box sx={{ borderTop: `1px solid ${colors.border}`, pt: 2 }}>
                    <Button
                      size="small"
                      onClick={() => setPayloadOpen(!payloadOpen)}
                      startIcon={<ExpandIcon sx={{ transform: payloadOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
                      sx={{ color: colors.textMuted, mb: 1, '&:hover': { color: colors.textSecondary } }}
                    >
                      Raw Payload Data
                    </Button>
                    <Collapse in={payloadOpen}>
                      <Box
                        sx={{
                          bgcolor: colors.bgDeepest,
                          border: `1px solid ${colors.border}`,
                          borderRadius: 1,
                          p: 2,
                          maxHeight: 300,
                          overflow: 'auto',
                          fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                          fontSize: '0.75rem',
                          color: colors.textSecondary,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {JSON.stringify(detailItem.canonicalItemData, null, 2)}
                      </Box>
                    </Collapse>
                  </Box>
                </Grid>
              )}
            </Grid>
          ) : (
            <Alert severity="error" sx={{ bgcolor: alpha(colors.error, 0.1), color: colors.error }}>
              Failed to load item details.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button onClick={() => setDetailOpen(false)} sx={{ color: colors.textSecondary }}>
            Close
          </Button>
          {detailItem?.status === 'pending' && detailItem.type === 'field_update' && (
            <>
              {/* Approve All field changes */}
              <Button
                variant="contained"
                disabled={(detailItem.fieldChanges?.filter(isBulkApprovable).length ?? 0) === 0}
                onClick={() => {
                  const allIds = detailItem.fieldChanges
                    ?.filter(isBulkApprovable)
                    .flatMap(fc => fc.allIds?.length ? fc.allIds : [fc.id as number]) ?? [];
                  if (allIds.length > 0) handleApproveFieldChanges(allIds);
                }}
                sx={{
                  bgcolor: colors.success,
                  color: colors.bgDeepest,
                  fontWeight: 700,
                  '&:hover': { bgcolor: colors.success, boxShadow: `0 0 20px ${alpha(colors.success, 0.4)}` },
                }}
              >
                Approve All ({detailItem.fieldChanges?.filter(isBulkApprovable).length ?? 0})
              </Button>
              {/* Approve Selected — only when partial selection */}
              {selectedFieldChangeIds.size > 0 &&
                selectedFieldChangeIds.size < (detailItem.fieldChanges?.filter(isBulkApprovable).length ?? 0) && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    // Expand selected representative IDs to include all duplicate IDs
                    const expandedIds = detailItem.fieldChanges
                      ?.filter(fc => isBulkApprovable(fc) && selectedFieldChangeIds.has(fc.id as number))
                      .flatMap(fc => fc.allIds?.length ? fc.allIds : [fc.id as number]) ?? [];
                    handleApproveFieldChanges(expandedIds);
                  }}
                  sx={{
                    borderColor: alpha(colors.success, 0.5),
                    color: colors.success,
                    fontWeight: 700,
                    '&:hover': { borderColor: colors.success, bgcolor: alpha(colors.success, 0.05) },
                  }}
                >
                  Approve Selected ({selectedFieldChangeIds.size})
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => { setDetailOpen(false); setRejectSku(detailItem.sku); }}
                sx={{
                  borderColor: alpha(colors.error, 0.5),
                  color: colors.error,
                  '&:hover': { borderColor: colors.error, bgcolor: alpha(colors.error, 0.05) },
                }}
              >
                Reject
              </Button>
            </>
          )}
          {detailItem?.status === 'pending' && detailItem.type !== 'field_update' && (
            <>
              {/* Edit stays on this dialog rather than closing it: the usual flow is
                  tweak → save → approve, and reopening the item between the two steps
                  is friction for no benefit. */}
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => handleOpenEdit(detailItem)}
                sx={{
                  borderColor: alpha(colors.info, 0.5),
                  color: colors.info,
                  '&:hover': { borderColor: colors.info, bgcolor: alpha(colors.info, 0.05) },
                }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                onClick={() => { setDetailOpen(false); requestApprove([detailItem.sku], false, detailItem); }}
                sx={{
                  bgcolor: colors.success,
                  color: colors.bgDeepest,
                  fontWeight: 700,
                  '&:hover': { bgcolor: colors.success, boxShadow: `0 0 20px ${alpha(colors.success, 0.4)}` },
                }}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                onClick={() => { setDetailOpen(false); setRejectSku(detailItem.sku); }}
                sx={{
                  borderColor: alpha(colors.error, 0.5),
                  color: colors.error,
                  '&:hover': { borderColor: colors.error, bgcolor: alpha(colors.error, 0.05) },
                }}
              >
                Reject
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ================= Edit staged item ================= */}
      <StagedItemEditDialog
        open={!!editItem}
        item={editItem}
        onClose={() => setEditItem(null)}
        onSaved={handleEditSaved}
      />

      <ResolveUnmappedCategoryDialog
        vendCategory={resolveVendCategory}
        onClose={() => setResolveVendCategory(null)}
        onResolved={refreshAll}
      />
    </Box>
  );
};

export default VaperiteStagingDashboard;
